import React, { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import { signOutUser } from './utils/authHelpers';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  getDocs,
  writeBatch,
  getDoc,
  arrayUnion,
  arrayRemove,
  orderBy,
  Timestamp,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getAffiliateLink, trackClick, isValidProductUrl, isBannedWordExact } from "./utils/affiliateUtils";
import { prepareImageBlob, moderateImageBlob } from './utils/imagePipeline';

import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { FaHeart } from "react-icons/fa";
import Onboarding from "./Onboarding";
import { useTranslation } from 'react-i18next';
import { validateTags, getTagCount, validateWishlistName } from './tagValidation';
import { FaFacebook, FaInstagram, FaTiktok, FaWhatsapp, FaShare } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import CollaboratorsModal from "./CollaboratorsModal";
import LikesModal from "./LikesModal";
import LazyImage from './LazyImage';
// Firebase Functions will be imported dynamically when needed
import { loadStripe } from "@stripe/stripe-js";
import PremiumModal from "./PremiumModal";
import { checkWishlistLimit } from "./wishlistUtils";
import WishlistSpaceLimitModal from './WishlistSpaceLimitModal';
import { isWishlistAtLimit, isWishlistNearLimit } from './utils/storageUtils';
import { checkDatabaseStorageLimit } from './utils/databaseLimits';
import DatabaseLimitModal from "./DatabaseLimitModal";
import { getProductsSortedByAddedAtDesc } from "./utils/productSorter";
import LikesManager from './utils/likesManager';
import { getShareableWishlistUrl } from './utils/getWebUrl';
import { isInvalidProductTitle, processProductUrl } from './utils/productAddition';
import { openExternalLink } from './utils/openExternalLink';

function dataURItoBlob(dataURI) {
  const byteString = atob(dataURI.split(",")[1]);
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], { type: mimeString });
}

// 🛡️ UNIFIED MODERATION SYSTEM for Apple App Store compliance
const moderateImage = async (base64Image, context = "", retryCount = 0) => {
  try {
    // Import unified moderation system
    const { moderateContent } = await import('./utils/contentModeration.js');

    // Convert base64 to blob for unified system
    const base64Data = base64Image.split(',')[1];
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'image/jpeg' });

    // Use unified moderation
    const result = await moderateContent(blob, context);

    console.log("🛡️ Unified moderation result:", result.allowed, "Context:", context);
    return result.allowed;

  } catch (error) {
    console.error("❌ Error in unified moderation:", error, "Context:", context);

    // FAIL SAFE: Block content if moderation fails
    return false;
  }
};

export default function Home() {
  const { t } = useTranslation();
  const [wishlistLimitReached, setWishlistLimitReached] = useState(false);

  const [showSpaceLimitModal, setShowSpaceLimitModal] = useState(false);
  const [showDatabaseLimitModal, setShowDatabaseLimitModal] = useState(false);
  const [databaseStorageInfo, setDatabaseStorageInfo] = useState(null);
  const [allowAddWithoutImage, setAllowAddWithoutImage] = useState(false);


  const [currentUserId, setCurrentUserId] = useState(null);
  const [wishlists, setWishlists] = useState([]);
  const [isLoadingWishlists, setIsLoadingWishlists] = useState(true);
  const [showFirstProductModal, setShowFirstProductModal] = useState(false);
  const [currentWishlistIdForModal, setCurrentWishlistIdForModal] = useState(null);
  const [newWishlistName, setNewWishlistName] = useState("");
  const [newWishlistTags, setNewWishlistTags] = useState("");
  const [newWishlistEventName, setNewWishlistEventName] = useState("");
  const [newWishlistDate, setNewWishlistDate] = useState("");
  const [newProductLinks, setNewProductLinks] = useState({});
  const [addingProductId, setAddingProductId] = useState(null);
  const [editTagsId, setEditTagsId] = useState(null);
  const [editedTags, setEditedTags] = useState("");
  const [editWishlistId, setEditWishlistId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editEventName, setEditEventName] = useState("");
  const [editEventDate, setEditEventDate] = useState("");
  const [expandedLists, setExpandedLists] = useState({});
  const [shareModalId, setShareModalId] = useState(null);
  const [wishlistToShare, setWishlistToShare] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sortOption, setSortOption] = useState("recenti");
  const [searchTerm, setSearchTerm] = useState("");
  const [showBannedModal, setShowBannedModal] = useState(false);
  const [navbarVisible, setNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [manualModeId, setManualModeId] = useState(null);
  const [manualModalOpenId, setManualModalOpenId] = useState(null);
  const [manualTitle, setManualTitle] = useState("");
  const [manualImage, setManualImage] = useState("");
  const [manualUrl, setManualUrl] = useState("");
  const [manualError, setManualError] = useState("");
  const navigate = useNavigate();
  const [tagError, setTagError] = useState("");
  const [followers, setFollowers] = useState(0);
  const [following, setFollowing] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [privacyConfirmId, setPrivacyConfirmId] = useState(null);
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [socialInstructionsModal, setSocialInstructionsModal] = useState(null);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(null);
  const [showLikesModal, setShowLikesModal] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showLeaveWishlistModal, setShowLeaveWishlistModal] = useState(false);
  const [wishlistToLeave, setWishlistToLeave] = useState(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('limit');
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [imageError, setImageError] = useState("");
  const [addImageModal, setAddImageModal] = useState({ show: false, productId: null, wishlistId: null, productTitle: null, productUrl: null });
  const [imageUploadType, setImageUploadType] = useState(null);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [spaceLimitWishlistId, setSpaceLimitWishlistId] = useState(null);
  const [inputUrl, setInputUrl] = useState("");
  const [currentError, setCurrentError] = useState("");
  const [isCreatingWishlist, setIsCreatingWishlist] = useState(false);
  const [showLifetimeLimitModal, setShowLifetimeLimitModal] = useState(false);


    const handleUpgradeToPremium = async () => {
        try {
            const functions = getFunctions();
            const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

            const result = await createCheckoutSession({
                priceId: 'price_1OQq9h2eZvKYlo2C8xG63y73' // Replace with your actual Price ID
            });

            const sessionId = result.data.id;

            const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);
            if (!stripe) {
                console.error("Stripe failed to load.");
                return;
            }

            const { error } = await stripe.redirectToCheckout({
                sessionId: sessionId,
            });

            if (error) {
                console.error("Error redirecting to Stripe Checkout:", error);
            }

        } catch (error) {
            console.error("Error calling createCheckoutSession:", error);
        }
    };

  // Mostra modale solo alla prima aggiunta per quella wishlist
  const handleFirstProductAdded = (wishlistId) => {
    const localKey = `firstProductShown-${wishlistId}`;
    if (!localStorage.getItem(localKey)) {
      localStorage.setItem(localKey, "true");
      setCurrentWishlistIdForModal(wishlistId);
      setShowFirstProductModal(true);
    }
  };

  const closeFirstProductModal = () => {
    setShowFirstProductModal(false);
  };

  const makeWishlistPublic = async () => {
    if (!currentWishlistIdForModal) return;

    try {
      await updateDoc(doc(db, "wishlists", currentWishlistIdForModal), {
        isPublic: true
      });
      console.log("✅ Wishlist resa pubblica:", currentWishlistIdForModal);
      closeFirstProductModal();
    } catch (error) {
      console.error("❌ Errore nel rendere pubblica la wishlist:", error);
    }
  };

  useEffect(() => {
    if (!currentUserId || !auth.currentUser) {
      setIsPremium(false);
      localStorage.setItem('userPremiumStatus', 'false');
      return;
    }

    let isActive = true; // Track if component is still mounted
    const userRef = doc(db, 'users', currentUserId);

    // Listener in tempo reale per lo stato Premium
    const unsubscribeUser = onSnapshot(userRef, (userDoc) => {
      // Check if still mounted and authenticated before processing
      if (!isActive || !auth.currentUser || auth.currentUser.uid !== currentUserId) {
        return;
      }

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const premiumStatus = userData.isPremium || false;
        setIsPremium(premiumStatus);

        // Aggiorna localStorage
        localStorage.setItem('userPremiumStatus', premiumStatus ? 'true' : 'false');

        console.log('✅ Premium status aggiornato in tempo reale:', premiumStatus);

        // Dispatch evento per altri componenti
        window.dispatchEvent(new CustomEvent('premiumStatusUpdated', {
          detail: { isPremium: premiumStatus }
        }));
      }
    }, (error) => {
      // Only log errors if component is still active and it's not a permission error
      if (isActive && error.code !== 'permission-denied') {
        console.error("🚫 Errore in premium status listener:", error);
      }
    });

    // Listener per aggiornamenti Premium da altri componenti (evita duplicati)
    const handlePremiumUpdate = (event) => {
      if (!isActive) return;
      
      const newStatus = event.detail.isPremium;
      if (newStatus !== isPremium) { // Solo se c'è un cambio di stato
        setIsPremium(newStatus);
        localStorage.setItem('userPremiumStatus', newStatus ? 'true' : 'false');
        console.log('🔄 Premium status cambiato:', newStatus);
      }
    };

    window.addEventListener('premiumStatusUpdated', handlePremiumUpdate);

    return () => {
      isActive = false; // Mark as inactive first
      unsubscribeUser();
      window.removeEventListener('premiumStatusUpdated', handlePremiumUpdate);
    };
  }, [currentUserId]);

  useEffect(() => {
    const checkIfNewUser = async () => {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.isNewUser) {
            setShowOnboarding(true);
          }
        }
      }
    };
    checkIfNewUser();
  }, []);

  useEffect(() => {
    if (!currentUserId || !auth.currentUser) {
      setFollowers(0);
      setFollowing(0);
      return;
    }

    let isActive = true; // Flag to track if component is still mounted
    const userRef = doc(db, "users", currentUserId);
    
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      // Check if still mounted and authenticated before processing
      if (!isActive || !auth.currentUser || auth.currentUser.uid !== currentUserId) {
        return;
      }

      if (docSnap.exists()) {
        const data = docSnap.data();
        setFollowers((data.followers || []).length);
        setFollowing((data.following || []).length);
      }
    }, (error) => {
      // Only log errors if component is still active and it's not a permission error
      if (isActive && error.code !== 'permission-denied') {
        console.error("🚫 Errore in onSnapshot user:", error);
      }
    });

    return () => {
      isActive = false; // Mark as inactive first
      console.log("🔄 Cleanup listener user");
      unsubscribe();
    };
  }, [currentUserId]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setNavbarVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setNavbarVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId || !auth.currentUser) {
      setWishlists([]);
      return;
    }

    let isSubscribed = true; // Flag per evitare aggiornamenti dopo unmount

    // Query per le wishlist dell'utente (proprietario)
    const ownWishlistsQuery = query(
      collection(db, "wishlists"),
      where("userId", "==", currentUserId)
    );

    // Query per le wishlist dove l'utente è collaboratore
    const collaboratorWishlistsQuery = query(
      collection(db, "wishlists"),
      where("collaborators", "array-contains", currentUserId)
    );

    let allWishlists = {
      own: [],
      collaborator: []
    };

    const updateWishlists = () => {
      // Verifica che l'utente sia ancora autenticato e il componente sia ancora montato
      if (!auth.currentUser || !isSubscribed) {
        console.log("🚫 Tentativo di aggiornamento senza autenticazione o componente smontato - bloccato");
        return;
      }
      
      // 🔧 FIX: Deduplicate wishlists by ID to prevent showing the same wishlist twice
      // This happens when user is both owner and collaborator on the same wishlist
      const merged = new Map();
      [...allWishlists.own, ...allWishlists.collaborator].forEach(w => {
        const prev = merged.get(w.id) || {};
        merged.set(w.id, { 
          ...prev, 
          ...w, 
          isOwner: prev.isOwner || w.isOwner, 
          isShared: prev.isShared || w.isShared 
        });
      });
      
      // ⚡ OPTIMIZED: Sort in memory by createdAt descending (newest first)
      const sortedWishlists = Array.from(merged.values()).sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const bDate = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return bDate - aDate;
      });
      
      setWishlists(sortedWishlists);
      setIsLoadingWishlists(false); // ⚡ Loading complete
    };

    const unsubscribeOwn = onSnapshot(ownWishlistsQuery, (snap) => {
      // Verifica che l'utente sia ancora autenticato e il componente sia ancora montato
      if (!auth.currentUser || !isSubscribed || auth.currentUser.uid !== currentUserId) {
        console.log("🚫 Snapshot own wishlists ricevuto senza autenticazione valida - ignorato");
        return;
      }

      allWishlists.own = snap.docs
        .filter(docSnap => {
          const data = docSnap.data();
          // Controlla sia 'archived' che 'isArchived' per compatibilità
          return !data.archived && !data.isArchived;
        })
        .map(docSnap => {
          const data = docSnap.data();
          const safeData = {
            isPublic: data.isPublic ?? false,
            createdAt: data.createdAt ?? new Date(),
            products: data.products ?? [],
            isOwner: true,
            ...data
          };
          const baseWishlist = {
            id: docSnap.id,
            ...safeData,
            open: true
          };
          // Use LikesManager to normalize the wishlist data
          const normalizedWishlist = LikesManager.normalizeWishlist(baseWishlist, currentUserId);
          return {
            ...normalizedWishlist,
            likeCount: normalizedWishlist.likesCount // Keep backward compatibility
          };
        });

      updateWishlists();
    }, (error) => {
      // Gestione migliorata degli errori per evitare log inutili
      if (error.code === 'permission-denied') {
        console.log("🔒 Permission denied su own wishlists - probabilmente logout in corso");
        // Non logare come errore, è normale durante il logout
      } else {
        console.error("🚫 Errore in onSnapshot own wishlists:", error);
      }
    });

    const unsubscribeCollaborator = onSnapshot(collaboratorWishlistsQuery, (snap) => {
      // Verifica che l'utente sia ancora autenticato e il componente sia ancora montato
      if (!auth.currentUser || !isSubscribed || auth.currentUser.uid !== currentUserId) {
        console.log("🚫 Snapshot collaborator wishlists ricevuto senza autenticazione valida - ignorato");
        return;
      }

      allWishlists.collaborator = snap.docs
        .filter(docSnap => {
          const data = docSnap.data();
          // Controlla sia 'archived' che 'isArchived' per compatibilità
          // 🔧 FIX: Exclude wishlists where user is the owner (prevents duplication)
          return !data.archived && !data.isArchived && data.userId !== currentUserId;
        })
        .map(docSnap => {
          const data = docSnap.data();
          const safeData = {
            isPublic: data.isPublic ?? false,
            createdAt: data.createdAt ?? new Date(),
            products: data.products ?? [],
            isOwner: false,
            isShared: true,
            ...data
          };
          const baseWishlist = {
            id: docSnap.id,
            ...safeData,
            open: true
          };
          // Use LikesManager to normalize the wishlist data
          const normalizedWishlist = LikesManager.normalizeWishlist(baseWishlist, currentUserId);
          return {
            ...normalizedWishlist,
            likeCount: normalizedWishlist.likesCount // Keep backward compatibility
          };
        });

      updateWishlists();
    }, (error) => {
      // Gestione migliorata degli errori per evitare log inutili
      if (error.code === 'permission-denied') {
        console.log("🔒 Permission denied su collaborator wishlists - probabilmente logout in corso");
        // Non logare come errore, è normale durante il logout
      } else {
        console.error("🚫 Errore in onSnapshot collaborator wishlists:", error);
      }
    });

    return () => {
      console.log("🔄 Cleanup listeners wishlists");
      isSubscribed = false; // Marca che il componente è stato smontato
      unsubscribeOwn();
      unsubscribeCollaborator();
    };
  }, [currentUserId]);



  // Check database storage limits with timeout
  const checkDatabaseLimits = async () => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('DATABASE_LIMIT_CHECK_TIMEOUT')), 5000)
      );
      
      const checkPromise = checkDatabaseStorageLimit(isPremium);
      const storageInfo = await Promise.race([checkPromise, timeoutPromise]);
      
      setDatabaseStorageInfo(storageInfo);

      if (storageInfo.isAtLimit) {
        setShowDatabaseLimitModal(true);
        return false; // Block action
      }

      if (storageInfo.isNearLimit) {
        // Show warning but allow action
        console.warn('🟡 Database storage near limit:', storageInfo);
      }

      return true; // Allow action
    } catch (error) {
      console.error('Error checking database limits:', error);
      if (error.message === 'DATABASE_LIMIT_CHECK_TIMEOUT') {
        console.warn('⚠️ Database limit check timed out, allowing action to proceed');
      }
      return true; // Allow action on error or timeout
    }
  };


  const handleCreateWishlist = async () => {
    if (!auth.currentUser) return;
    const user = auth.currentUser;

    try {
      // Controllo limiti wishlist
      console.log("🔍 Controllo limiti wishlist...");

      // 1. Controllo limite totale lifetime (50 per tutti)
      const totalWishlistsQuery = query(
        collection(db, "wishlists"),
        where("userId", "==", user.uid)
      );
      const totalSnapshot = await getDocs(totalWishlistsQuery);
      const totalWishlists = totalSnapshot.size;

      console.log(`📊 Wishlist totali dell'utente: ${totalWishlists}/50`);

      if (totalWishlists >= 50) {
        setShowLifetimeLimitModal(true);
        return;
      }

      // 2. Controllo limite attive per utenti non Premium (10)
      if (!isPremium) {
        const activeWishlistsQuery = query(
          collection(db, "wishlists"),
          where("userId", "==", user.uid),
          where("archived", "==", false)
        );
        const activeSnapshot = await getDocs(activeWishlistsQuery);
        const activeWishlists = activeSnapshot.size;

        console.log(`📊 Wishlist attive dell'utente non Premium: ${activeWishlists}/10`);

        if (activeWishlists >= 10) {
          setTagError(t('errors.wishlistLimitActive'));
          setPremiumModalReason('limit');
          setShowPremiumModal(true);
          return;
        }
      }

      // Validate wishlist name for Apple App Store compliance
      const nameValidation = validateWishlistName(newWishlistName);
      if (!nameValidation.isValid) {
        setTagError(t(`errors.${nameValidation.error}`));
        return;
      }

      if (!auth.currentUser) {
        setTagError(t('errors.mustBeLoggedIn'));
        return;
      }

      const tagsArray = newWishlistTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);

      // Validazione completa dei tag inclusa moderazione
      const tagValidation = validateTags(newWishlistTags);
      if (!tagValidation.isValid) {
        setTagError(t(`errors.${tagValidation.error}`));
        return;
      }

      if (tagsArray.length > 5) {
        setTagError(t('errors.tagLimit'));
        return;
      }

      console.log("🔄 Creazione wishlist iniziata...");
      const eventDate = newWishlistDate ? new Date(newWishlistDate + 'T23:59:59') : null;

      const wishlistData = {
        name: newWishlistName.trim(),
        eventName: newWishlistEventName.trim() || "",
        tags: tagsArray,
        eventDate: eventDate ? eventDate.toISOString() : null,
        userId: auth.currentUser.uid,
        isPublic: false,
        likes: [],
        products: [],
        createdAt: serverTimestamp(),
        archived: false
      };

      console.log("📋 Dati wishlist:", wishlistData);

      const docRef = await addDoc(collection(db, "wishlists"), wishlistData);
      console.log("✅ Wishlist creata con ID:", docRef.id);

      setNewWishlistName("");
      setNewWishlistTags("");
      setNewWishlistDate("");
      setNewWishlistEventName("");
      setShowCreateModal(false);
      setTagError("");
    } catch (error) {
      console.error("❌ Errore nella creazione della wishlist:", error);
      console.error("❌ Codice errore:", error.code);
      console.error("❌ Messaggio errore:", error.message);
      setTagError(`${t('errors.createWishlistError')}: ${error.message}`);
    }
  };



  // Funzione per aggiungere un prodotto manualmente
  const addProduct = async (productData) => {
    if (!productData.wishlistId || !productData.title) {
      console.error("Wishlist ID and title are required");
      return;
    }

    // Check database storage limits first
    const canProceed = await checkDatabaseLimits();
    if (!canProceed) {
      return; // Database limit reached, modal will be shown
    }

    // Existing logic for adding product would go here...
    // This is a placeholder, replace with actual add product logic if needed.
    console.log("Adding product with data:", productData);
  };

  const handleAddProduct = async (id) => {
    const url = newProductLinks[id]?.trim();
    if (!url) return;

    // Check database storage limits first
    const canProceed = await checkDatabaseLimits();
    if (!canProceed) {
      return; // Database limit reached, modal will be shown
    }

    console.log('🚀 HOME: Starting product parse with centralized utility for URL:', url);
    
    // Use centralized product URL processing
    const result = await processProductUrl(url);
    
    // Handle validation/processing errors
    if (!result.success) {
      if (result.errorKey === 'modals.bannedContent') {
        setShowBannedModal(true);
        resetAddBarFor(id);
        return;
      }
      
      if (result.needsManualMode) {
        console.log('⚠️ Auto-load failed, opening manual modal');
        setManualError(t(result.errorKey) || t('errors.autoLoadError'));
        setManualTitle("");
        setManualImage("");
        setManualModalOpenId(id);
        setManualUrl(url);
        return;
      }
      
      // URL validation failed
      alert(t(result.errorKey) || t('modals.bannedContent'));
      resetAddBarFor(id);
      return;
    }

    // Success - add product to wishlist
    const item = {
      title: result.title,
      image: result.image,
      url: result.affiliateUrl || url,
      reservedBy: null
    };

    try {
      // Verifica se è il primo prodotto prima di aggiungere
      const wishlist = wishlists.find(w => w.id === id);
      const isFirstProduct = !wishlist?.products || wishlist.products.length === 0;

      // Get current products and prepend new item
      const wishlistRef = doc(db, "wishlists", id);
      const wishlistDoc = await getDoc(wishlistRef);
      const currentProducts = wishlistDoc.exists() ? (wishlistDoc.data().products || []) : [];
      const updatedProducts = [item, ...currentProducts];
      
      await updateDoc(wishlistRef, {
        products: updatedProducts
      });

      // Mostra il modale solo se è il primo prodotto
      if (isFirstProduct) {
        handleFirstProductAdded(id);
      }

      resetAddBarFor(id);
      setManualError("");
      console.log('✅ HOME: Product added successfully via centralized utility');
    } catch (e) {
      console.error("Errore nel salvataggio:", e);
      setManualError(t('errors.autoLoadError'));
      setManualTitle("");
      setManualImage("");
      setManualModalOpenId(id);
      setManualUrl(url);
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      const wishlistRef = doc(db, "wishlists", productToDelete.wishlistId);
      const wishlistDoc = await getDoc(wishlistRef);

      if (!wishlistDoc.exists()) {
        console.error("Wishlist not found");
        return;
      }

      const wishlistData = wishlistDoc.data();
      const updatedProducts = wishlistData.products.filter((_, index) => index !== productToDelete.productIndex);

      await updateDoc(wishlistRef, {
        products: updatedProducts
      });

      // Aggiorna lo stato locale
      setWishlists(prev => prev.map(wishlist => {
        if (wishlist.id === productToDelete.wishlistId) {
          return {
            ...wishlist,
            products: updatedProducts
          };
        }
        return wishlist;
      }));

      setShowConfirmModal(false);
      setProductToDelete(null);
    } catch (error) {
      console.error("Errore durante l'eliminazione:", error);
    }
  };

  const handleProductClick = (product) => {
    // Navigazione al prodotto o apertura link
    if (product.url) {
      const affiliateUrl = getAffiliateLink(product);
      openExternalLink(affiliateUrl);
    }
  };

  const handleAddImage = async () => {
    try {
      await addImageToProduct();
    } catch (error) {
      console.error("❌ Error in handleAddImage:", error);
      if (error.message === 'TIMEOUT') {
        setImageError(t('errors.imageUploadTimeout'));
      } else if (error.message === 'DATABASE_TIMEOUT') {
        setImageError(t('errors.networkError'));
      } else {
        setImageError(t('errors.imageUploadError'));
      }
      setImageUploadLoading(false);
    }
  };

  const addImageToProduct = async () => {
    if (!addImageModal.show) return;

    setImageUploadLoading(true);
    setImageError("");

    try {
      // Check database storage limits first (with timeout to prevent hanging)
      console.log('🔄 Checking database storage limits...');
      const canProceed = await checkDatabaseLimits();
      if (!canProceed) {
        console.log('❌ Database limit reached, blocking image upload');
        setImageUploadLoading(false);
        return; // Database limit reached, modal will be shown
      }
      console.log('✅ Database limits check passed, proceeding with upload');

      // Check storage limit before processing
      const currentWishlist = wishlists.find(w => w.id === addImageModal.wishlistId);
      if (!currentWishlist) {
        setImageError(t('errors.wishlistNotFound'));
        setImageUploadLoading(false);
        return;
      }

      // Check if we're updating an existing product's image (should be allowed even at limit)
      const existingProduct = currentWishlist.products.find(product => {
        return product.title === addImageModal.productTitle && product.url === addImageModal.productUrl;
      });
      
      // Only block if at limit AND the product doesn't already have an image
      // (replacing existing image doesn't increase storage significantly)
      if (isWishlistAtLimit(currentWishlist.products || []) && existingProduct && !existingProduct.image) {
        setImageUploadLoading(false);
        setImageError("");
        setShowSpaceLimitModal(true);
        setSpaceLimitWishlistId(addImageModal.wishlistId);
        return;
      }

      let finalImageUrl = "";

      // Handle different image upload types
      if (imageUploadType === 'url' && newImageUrl.trim()) {
        // For URL uploads, do basic validation without network request to avoid CORS/HTTP errors
        try {
          const url = new URL(newImageUrl.trim());
          
          // Basic URL structure validation
          if (!['http:', 'https:'].includes(url.protocol)) {
            throw new Error('Invalid protocol');
          }

          finalImageUrl = newImageUrl.trim();
          console.log("✅ URL validation passed:", finalImageUrl);
          
        } catch (error) {
          console.error("❌ URL validation error:", error);
          setImageError("URL non valido. Inserisci un link diretto all'immagine.");
          setImageUploadLoading(false);
          return;
        }
      } else if (imageUploadType === 'file' && selectedImageFile) {
        // Process and upload the image file with optimized pipeline
        try {
          console.log("📤 Starting image processing for file:", selectedImageFile.name);

          // Validate file size
          if (selectedImageFile.size > 10 * 1024 * 1024) { // 10MB limit
            setImageError("File troppo grande. Massimo 10MB.");
            setImageUploadLoading(false);
            return;
          }

          // Process image with timeout
          console.log("🔄 Processing image...");
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('PROCESSING_TIMEOUT')), 30000)
          );
          
          const processPromise = prepareImageBlob(selectedImageFile);
          const processedImage = await Promise.race([processPromise, timeoutPromise]);
          
          console.log("✅ Image processed successfully, size:", Math.round(processedImage.blob.size / 1024), "KB");

          // 🛡️ CRITICAL: Moderate the processed image - Apple App Store compliance
          console.log("🔍 Moderating processed image...");
          const isAllowed = await moderateImageBlob(processedImage.blob);
          if (!isAllowed) {
            console.warn("⚠️ Image blocked by moderation system");
            setImageError(t('errors.imageNotAllowed'));
            setImageUploadLoading(false);
            return;
          }
          console.log("✅ Image passed moderation checks");

          // Convert to base64 for Firestore storage
          console.log("🔄 Converting to base64...");
          const arrayBuffer = await processedImage.blob.arrayBuffer();
          const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
          finalImageUrl = `data:${processedImage.mime};base64,${base64}`;
          console.log("✅ Image converted to base64 successfully, size:", Math.round(finalImageUrl.length / 1024), "KB");
        } catch (error) {
          console.error("❌ Error processing/uploading image:", error);
          let errorMessage = t('errors.imageProcessingError');

          if (error.message === 'PROCESSING_TIMEOUT') {
            errorMessage = t('errors.imageUploadTimeout');
          } else if (error.message?.includes('storage') || error.message?.includes('quota')) {
            errorMessage = t('errors.databaseStorageLimit');
          } else if (error.code === 'storage/quota-exceeded') {
            errorMessage = t('errors.databaseStorageLimit');
          } else if (error === 'invalid_mime') {
            errorMessage = t('errors.invalidFileType');
          }

          setImageError(errorMessage);
          setImageUploadLoading(false);
          return;
        }
      }

      if (!finalImageUrl) {
        setImageError(t('errors.noImageSelected'));
        setImageUploadLoading(false);
        return;
      }

      // Find the product to update by exact matching with better logging
      console.log("📝 Searching for product to update:", {
        searchTitle: addImageModal.productTitle,
        searchUrl: addImageModal.productUrl,
        totalProducts: currentWishlist.products.length,
        allProductTitles: currentWishlist.products.map(p => p.title)
      });

      const productIndex = currentWishlist.products.findIndex(product => {
        const titleMatch = product.title === addImageModal.productTitle;
        const urlMatch = product.url === addImageModal.productUrl;
        console.log("🔍 Product match check:", {
          productTitle: product.title,
          titleMatch,
          productUrl: product.url,
          urlMatch,
          overallMatch: titleMatch && urlMatch
        });
        return titleMatch && urlMatch;
      });

      if (productIndex === -1) {
        console.error("❌ Product not found. Available products:", 
          currentWishlist.products.map(p => ({ title: p.title, url: p.url }))
        );
        setImageError("Prodotto non trovato nella wishlist");
        setImageUploadLoading(false);
        return;
      }

      console.log("✅ Found product at index:", productIndex);

      // Create updated products array with proper spread
      const updatedProducts = currentWishlist.products.map((product, index) => {
        if (index === productIndex) {
          return {
            ...product,
            image: finalImageUrl
          };
        }
        return product;
      });

      console.log("📝 Updating product at index:", productIndex, "with image:", finalImageUrl.substring(0, 50) + "...");

      // Update Firebase with timeout protection
      const updateTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('DATABASE_TIMEOUT')), 15000)
      );
      
      const wishlistRef = doc(db, "wishlists", addImageModal.wishlistId);
      const updatePromise = updateDoc(wishlistRef, {
        products: updatedProducts
      });
      
      await Promise.race([updatePromise, updateTimeoutPromise]);
      console.log("✅ Database updated successfully");

      // Update local state immediately
      setWishlists(prev => prev.map(w =>
        w.id === addImageModal.wishlistId
          ? { ...w, products: updatedProducts }
          : w
      ));

      console.log("✅ Local state updated successfully");

      // Reset and close modal
      setAddImageModal({ show: false, productId: null, wishlistId: null, productTitle: null, productUrl: null });
      setImageUploadType(null);
      setNewImageUrl("");
      setSelectedImageFile(null);
      setImageUploadLoading(false);
      setImageError("");

    } catch (error) {
      console.error("❌ Errore durante l'aggiunta immagine:", error);
      let errorMessage = t('errors.imageUploadError') || 'Error during image upload';

      if (error.message === 'DATABASE_TIMEOUT') {
        errorMessage = 'Timeout database. Riprova più tardi.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Servizio temporaneamente non disponibile. Riprova.';
      } else if (error.code === 'permission-denied') {
        errorMessage = 'Permessi insufficienti. Ricarica la pagina.';
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'Operazione troppo lenta. Riprova.';
      } else if (error.code === 'not-found') {
        errorMessage = 'Wishlist non trovata';
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = 'Quota di archiviazione superata. Elimina alcune immagini o aggiorna a Premium.';
      } else if (error.message?.includes('quota') || error.message?.includes('limit')) {
        errorMessage = 'Limite di archiviazione raggiunto. Elimina contenuti o aggiorna a Premium.';
      }

      setImageError(errorMessage);
      setImageUploadLoading(false);
    }
  };

  const toggleExpand = (id) => {
    const wishlistElement = document.querySelector(`[data-wishlist-id="${id}"]`);
    const currentScrollY = window.scrollY;
    const elementTop = wishlistElement ? wishlistElement.offsetTop : 0;

    setExpandedLists(prev => {
      const wasExpanded = prev[id];

      if (wasExpanded) {
        setTimeout(() => {
          const headerOffset = 120;
          window.scrollTo({
            top: elementTop - headerOffset,
            behavior: 'smooth'
          });
        }, 50);
      }

      return { ...prev, [id]: !prev[id] };
    });
  };

  const toggleLike = async (id) => {
    const uid = auth.currentUser.uid;
    const currentWishlist = wishlists.find(x => x.id === id);
    
    if (!currentWishlist) {
      console.log("❌ Wishlist non trovata:", id);
      return;
    }

    try {
      // Use the unified likes manager to handle the toggle
      const updatedWishlist = await LikesManager.toggleLike(
        id, 
        uid, 
        currentWishlist
      );

      // Home.jsx uses real-time listeners, so the state updates automatically
      // No need to manually update state here as onSnapshot will handle it
      console.log("✅ Like aggiornato in Home.jsx");
    } catch (error) {
      console.error("❌ Errore toggle like in Home.jsx:", error);
    }
  };

  const handleUpdateTags = async (id) => {
    setTagError("");

    const tagsArray = editedTags.split(",").map(t => t.trim()).filter(Boolean);
    const forbidden = ["parolaccia1", "parolaccia2", "xxx", "offensivo", "porno"];

    const containsForbidden = tagsArray.some(tag => forbidden.includes(tag.toLowerCase()));
    if (containsForbidden) {
      setTagError(t('errors.forbiddenTags'));
      return;
    }

    if (tagsArray.length > 5) {
      setTagError(t('errors.tagLimit'));
      return;
    }

    await updateDoc(doc(db, "wishlists", id), { tags: tagsArray });
    setEditTagsId(null);
    setEditedTags("");
  };

  const confirmDeleteWishlist = async () => {
    if (!deleteConfirmId) return;
    await deleteDoc(doc(db, "wishlists", deleteConfirmId));
    setDeleteConfirmId(null);
  };

  const confirmTogglePrivacy = async () => {
    if (!privacyConfirmId) return;
    const w = wishlists.find(x => x.id === privacyConfirmId);
    if (!w) return;
    await updateDoc(doc(db, "wishlists", w.id), { isPublic: !w.isPublic });
    setPrivacyConfirmId(null);
  };

  const confirmArchiveWishlist = async () => {
    if (!archiveConfirmId) return;
    await updateDoc(doc(db, "wishlists", archiveConfirmId), { archived: true });
    setArchiveConfirmId(null);
  };

  const handleLeaveWishlist = async (wishlistId) => {
    if (!auth.currentUser) return;
    
    // Show confirmation modal
    setWishlistToLeave(wishlistId);
    setShowLeaveWishlistModal(true);
  };

  const confirmLeaveWishlist = async () => {
    if (!auth.currentUser || !wishlistToLeave || isLeaving) return;
    
    setIsLeaving(true);
    try {
      const wishlistRef = doc(db, "wishlists", wishlistToLeave);
      const wishlistDoc = await getDoc(wishlistRef);
      
      if (!wishlistDoc.exists()) {
        console.error("Wishlist not found");
        return;
      }
      
      const currentCollaborators = wishlistDoc.data().collaborators || [];
      const updatedCollaborators = currentCollaborators.filter(
        collaboratorId => collaboratorId !== auth.currentUser.uid
      );
      
      // Update the wishlist in Firestore
      await updateDoc(wishlistRef, {
        collaborators: updatedCollaborators
      });
      
      // Remove the wishlist from local state
      setWishlists(prev => prev.filter(w => w.id !== wishlistToLeave));
      
      console.log("Successfully left wishlist:", wishlistToLeave);
      
      // Close modal and clear state
      setShowLeaveWishlistModal(false);
      setWishlistToLeave(null);
    } catch (error) {
      console.error("Error leaving wishlist:", error);
      alert(t('errors.leaveWishlistError') || 'Error leaving wishlist');
      // Close modal on error too
      setShowLeaveWishlistModal(false);
      setWishlistToLeave(null);
    } finally {
      setIsLeaving(false);
    }
  };

  const closeLeaveWishlistModal = () => {
    if (isLeaving) return; // Prevent closing during operation
    setShowLeaveWishlistModal(false);
    setWishlistToLeave(null);
  };

  const handleShare = async (wishlistId) => {
    const wishlist = wishlists.find(w => w.id === wishlistId);

    if (wishlist && wishlist.sharedLink !== true) {
      try {
        await updateDoc(doc(db, "wishlists", wishlistId), { sharedLink: true });
        console.log("🔗 sharedLink impostato a true per wishlist:", wishlistId);
      } catch (error) {
        console.error("Errore impostazione sharedLink:", error);
      }
    }

    setShareModalId(wishlistId);
    setWishlistToShare(wishlist);
  };

  const handleCopyLink = () => {
    if (!shareModalId) return;

    const link = getShareableWishlistUrl(shareModalId);
    console.log("🔗 Condivisione wishlist:", { id: shareModalId, link });

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => {
          setCopySuccess(true);
          console.log("Link copiato con navigator.clipboard:", link);
        })
        .catch((error) => {
          console.error("Errore copia con clipboard:", error);
          fallbackCopyTextToClipboard(link);
        });
    } else {
      fallbackCopyTextToClipboard(link);
    }
  };

  const handleSocialShare = (platform) => {
    if (!shareModalId || !wishlistToShare) return;

    const shareUrl = getShareableWishlistUrl(shareModalId);
    const shareText = `${t('share.checkOut')} "${wishlistToShare.name}" ${t('share.onWishare')}`;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

    let socialUrl = '';

    switch(platform) {
      case 'facebook':
        socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'instagram':
        handleCopyLink();
        setSocialInstructionsModal('instagram');
        return;
      case 'tiktok':
        handleCopyLink();
        setSocialInstructionsModal('tiktok');
        return;
      case 'whatsapp':
        if (isMobile) {
          const whatsappText = encodeURIComponent(shareText + ' ' + shareUrl);
          socialUrl = `whatsapp://send?text=${whatsappText}`;

          const link = document.createElement('a');
          link.href = socialUrl;
          link.click();

          setTimeout(() => {
            const waLink = document.createElement('a');
            waLink.href = `https://wa.me/?text=${whatsappText}`;
            waLink.target = '_blank';
            waLink.click();
          }, 500);

          return;
        } else {
          socialUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        }
        break;
      case 'twitter':
        socialUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      default:
        return;
    }

    if (socialUrl) {
      try{
        if (platform === 'whatsapp' && !isMobile) {
          const newWindow = window.open(socialUrl, '_blank', 'width=800,height=60');

          setTimeout(() => {
            if (!newWindow || newWindow.closed) {
              handleCopyLink();
              setSocialInstructionsModal('whatsapp');
            }
          }, 2000);
        } else if (platform !== 'whatsapp') {
          const newWindow = window.open(socialUrl, '_blank', 'width=600,height=400');

          setTimeout(() => {
            if (!newWindow || newWindow.closed) {
              handleCopyLink();
              setSocialInstructionsModal(platform);
            }
          }, 1000);
        }

      } catch (error) {
        console.error('Errore nell\'apertura della condivisione:', error);
        handleCopyLink();
        setSocialInstructionsModal(platform);
      }
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopySuccess(true);
        console.log("Link copiato con fallback:", text);
      } else {
        console.error("Fallback copy failed");
      }
    } catch (err) {
      console.error('Fallback copy error:', err);
    }

    document.body.removeChild(textArea);
  };

  const getCountdown = (date) => {
    if (!date) return null;
    const target = new Date(date);
    const now = new Date();
    const diff = target - now;
    if (diff <= 0) return t('wishlist.eventPassed');
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    return `${days}g ${hours}h ${minutes}m`;
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      const wasLoggedOut = !user && currentUserId; // Detect logout

      // If logging out, immediately clean up state to prevent race conditions
      if (wasLoggedOut) {
        console.log("🔄 Utente disconnesso - pulizia immediata dello stato");
        setCurrentUserId(null);
        setWishlists([]);
        setFollowers(0);
        setFollowing(0);
        setExpandedLists({});
        setNewProductLinks({});
        setAddingProductId(null);
        setEditTagsId(null);
        setEditWishlistId(null);
        setMenuOpenId(null);
        setShareModalId(null);
        setManualModalOpenId(null);
        setShowCollaboratorsModal(null);
        return; // Early return to prevent further processing
      }

      setCurrentUserId(user ? user.uid : null);

      // Clean up state if no user (but not during logout to avoid double cleanup)
      if (!user && !wasLoggedOut) {
        setWishlists([]);
        setFollowers(0);
        setFollowing(0);
        setExpandedLists({});
        setNewProductLinks({});
        setAddingProductId(null);
        setEditTagsId(null);
        setEditWishlistId(null);
        setMenuOpenId(null);
        setShareModalId(null);
        setManualModalOpenId(null);
        setShowCollaboratorsModal(null);
      }
    });
    return () => unsubscribe();
  }, [currentUserId]);

  const openUserPage = (userId) => {
    const currentScrollY = window.scrollY;
    sessionStorage.setItem('fromScroll', currentScrollY.toString());
    sessionStorage.setItem('fromPage', '/');
    navigate(`/utente/${userId}`, {
      state: {
        from: "/"
      }
    });
  };

  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('fromScroll');
    const fromPage = sessionStorage.getItem('fromPage');
    const fromBottomNav = location.state?.fromBottomNav;
    const shouldRestore = !fromBottomNav && savedScrollPosition && fromPage === '/';

    if (shouldRestore && wishlists.length > 0) {
      const scrollPosition = parseInt(savedScrollPosition, 10);

      const timeoutId = setTimeout(() => {
        if (!isNaN(scrollPosition)) {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });

          sessionStorage.removeItem('fromScroll');
          sessionStorage.removeItem('fromPage');
        }
      }, 800);

      return () => clearTimeout(timeoutId);
    }
  }, [wishlists, location.state]);

  const handleReactivateWishlist = async (wishlistId) => {
    if (!auth.currentUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      const isPremium = userDoc.exists() ? userDoc.data().isPremium || false : false;

      console.log("🔍 Controllo limiti per riattivazione wishlist...");

      // 1. Controllo limite totale lifetime (50 per tutti) - non necessario per riattivazione
      // perché la wishlist esiste già, non ne stiamo creando una nuova

      // 2. Controllo limite attive per utenti non Premium (10)
      if (!isPremium) {
        const activeWishlistsQuery = query(
          collection(db, "wishlists"),
          where("userId", "==", auth.currentUser.uid),
          where("archived", "==", false)
        );
        const activeSnapshot = await getDocs(activeWishlistsQuery);
        const activeWishlists = activeSnapshot.size;

        console.log(`📊 Wishlist attive prima della riattivazione: ${activeWishlists}/10`);

        if (activeWishlists >= 10) {
          setShowLimitModal(true);
          return;
        }
      }

      // Procedi con la riattivazione
      await updateDoc(doc(db, 'wishlists', wishlistId), {
        archived: false,
        reactivatedAt: new Date()
      });

      console.log("✅ Wishlist riattivata con successo");

      // Aggiorna la lista locale
      setWishlists(prev => prev.map(w =>
        w.id === wishlistId ? { ...w, archived: false } : w
      ));

    } catch (error) {
      console.error("❌ Errore riattivazione:", error);
      alert(t('errors.reactivationError'));
    }
  };

  // ---- FUNZIONE COMPRESSIONE IMMAGINI ULTRA-AGGRESSIVA ----
    const compressImage = (file, maxSize = 200, quality = 0.3) => {
      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          // Dimensioni molto più ridotte
          let { width, height } = img;
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);

          // Compressione ultra-compressiva - limite 150KB
          const tryCompress = (currentQuality) => {
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error("Errore nella compressione"));
                return;
              }

              console.log(`📊 Tentativo compressione: ${blob.size} bytes con qualità ${currentQuality}`);

              // Limite severissimo: 150KB per essere sicuri
              if (blob && blob.size > 150000 && currentQuality > 0.05) {
                console.log(`🗜️ Riducendo qualità da ${currentQuality} a ${(currentQuality - 0.05).toFixed(2)}`);
                tryCompress(currentQuality - 0.05);
              } else if (blob.size > 150000) {
                reject(new Error("Immagine troppo grande anche con compressione massima"));
              } else {
                console.log(`✅ Immagine compressa: ${blob.size} bytes`);

                // Converti blob a base64
                const reader = new FileReader();
                reader.onload = () => {
                  const base64 = reader.result;
                  // Controllo finale della dimensione base64
                  if (base64.length > 200000) {
                    reject(new Error("Base64 risultante troppo grande"));
                  } else {
                    console.log(`✅ Base64 finale: ${base64.length} caratteri`);
                    resolve(base64);
                  }
                };
                reader.onerror = () => reject(new Error("Errore conversione base64"));
                reader.readAsDataURL(blob);
              }
            }, 'image/jpeg', currentQuality);
          };

          tryCompress(quality);
        };

        img.onerror = () => reject(new Error("Errore nel caricamento dell'immagine"));
        img.src = URL.createObjectURL(file);
      });
    };

  // Firebase error mapping function
  function mapFirebaseErrorToKey(code, message = '') {
    switch (code) {
      case 'permission-denied':
      case 'unauthenticated':
        return 'errors.saveErrorInsufficientPermissions';
      case 'unavailable':
        return 'errors.networkError';
      case 'invalid-argument':
        return 'errors.formValidationFailed';
      case 'resource-exhausted':
        return 'errors.storageQuotaExceeded';
      case 'STORAGE_LIMIT_EXCEEDED':
        return 'errors.storageQuotaExceeded';
      case 'FILE_TOO_LARGE':
        return 'errors.fileTooLarge';
      default:
        // Check if message contains storage-related keywords
        if (message && (message.includes('storage') || message.includes('quota') || message.includes('limit'))) {
          return 'errors.storageQuotaExceeded';
        }
        return 'errors.saveErrorGeneric';
    }
  }

  // Safe translation function with fallback
  function tSafe(keyOrText) {
    if (!keyOrText || typeof keyOrText !== 'string') return '';
    const s = String(keyOrText);
    // If it contains whitespace, it's likely already human text
    if (/\s/.test(s)) return s;
    // Otherwise, try translate key
    return t(s);
  }

  // Reset function for specific add product bar
  const resetAddBarFor = (id) => {
    setNewProductLinks(p => ({ ...p, [id]: "" }));
    setAddingProductId(null);
    setManualTitle("");
    setManualImage("");
    setManualError("");
    setManualModeId(null);
    setManualModalOpenId(null);
    setInputUrl("");
  };

  // Reset function for add product bar
  const resetAddBar = () => {
    const id = addingProductId || manualModalOpenId;
    if (id) resetAddBarFor(id);
  };

  const [manualLoading, setManualLoading] = useState(false);

  const handleManualSubmit = async (e) => {
    e?.preventDefault?.();
    if (manualLoading) return;

    setManualLoading(true);
    setManualError('');

    try {
      // 1) Check database storage limits first (with timeout)
      console.log('🔄 Checking database storage limits for manual submission...');
      const canProceed = await checkDatabaseLimits();
      if (!canProceed) {
        console.log('❌ Database limit reached, blocking manual submission');
        setManualLoading(false);
        return; // Database limit reached, modal will be shown
      }
      console.log('✅ Database limits check passed for manual submission');

      // 2) Validate required fields - title is required
      if (!manualTitle?.trim()) {
        setManualError(t('errors.titleRequired'));
        setManualLoading(false);
        return;
      }

      // ✅ Parole bannate rimosso - solo Azure modererà il contenuto inappropriato

      // 4) Unified content moderation - text and image must pass
      let finalImageUrl = null;
      try {
        console.log("🔍 Unified moderation for manual product:", { title: manualTitle.substring(0, 50), hasImage: !!manualImage });
        
        // Always moderate text
        const { moderateText } = await import('./utils/contentModeration.js');
        const textResult = await moderateText(manualTitle.trim(), "product");
        if (!textResult.allowed) {
          console.warn("⚠️ Text blocked by unified moderation:", {
            title: manualTitle.substring(0, 50),
            reason: textResult.reason
          });
          setShowBannedModal(true);
          setManualModalOpenId(null);
          setManualLoading(false);
          return;
        }
        
        // Moderate image if present
        if (manualImage) {
          const imageAllowed = await moderateImage(manualImage, "product");
          if (!imageAllowed) {
            console.warn("⚠️ Image blocked by unified moderation:", { title: manualTitle.substring(0, 50) });
            setShowBannedModal(true);
            setManualModalOpenId(null);
            setManualLoading(false);
            return;
          }
          finalImageUrl = manualImage;
        }
        
        console.log("✅ Manual product passed unified moderation");
      } catch (moderationError) {
        console.warn("⚠️ Unified moderation error, content blocked for safety:", moderationError);
        setManualError(t('errors.moderationError') || 'Moderation error');
        setManualLoading(false);
        return;
      }

      // 5) Create product in the wishlist
      if (!auth.currentUser?.uid) {
        throw { code: 'permission-denied' };
      }

      const item = {
        title: manualTitle.trim(),
        image: finalImageUrl || null,
        url: newProductLinks[manualModalOpenId] || "",
        reservedBy: null
      };

      // Check if it's the first product before adding
      const wishlist = wishlists.find(w => w.id === manualModalOpenId);
      const isFirstProduct = !wishlist?.products || wishlist.products.length === 0;

      console.debug('[WRITE]', {
        wishlistId: manualModalOpenId,
        path: `wishlists/${manualModalOpenId}`,
        payload: Object.keys(item),
        operation: 'prepend to products array (newest first)'
      });

      // Get current products and prepend new item
      const wishlistRef = doc(db, "wishlists", manualModalOpenId);
      const wishlistDoc = await getDoc(wishlistRef);
      const currentProducts = wishlistDoc.exists() ? (wishlistDoc.data().products || []) : [];
      const updatedProducts = [item, ...currentProducts];
      
      await updateDoc(wishlistRef, {
        products: updatedProducts
      });

      // Show modal only if it's the first product
      if (isFirstProduct) {
        handleFirstProductAdded(manualModalOpenId);
      }

      // 6) Success: close modal and reset
      resetAddBar();

    } catch (error) {
      console.error("Errore aggiunta prodotto:", error);
      const key = mapFirebaseErrorToKey(error?.code, error?.message);
      let errorMessage = t(key);
      
      // Add specific handling for storage/quota errors
      if (error?.code === 'storage/quota-exceeded' || 
          error?.message?.includes('quota') || 
          error?.message?.includes('storage') ||
          error?.message?.includes('limit')) {
        errorMessage = t('errors.databaseStorageLimit');
      }
      
      setManualError(errorMessage);
    } finally {
      setManualLoading(false);
    }
  };

  // Check storage limit before proceeding with image extraction
    const addFromUrl = async (wishlistId) => {
        if (!inputUrl.trim()) {
            setCurrentError(t('errors.linkRequired'));
            return;
        }

        // Check storage limit before proceeding
        const wishlist = wishlists.find(w => w.id === wishlistId);
        if (wishlist && isWishlistAtLimit(wishlist.products || []) && !allowAddWithoutImage) {
            setSpaceLimitWishlistId(wishlistId);
            setShowSpaceLimitModal(true);
            return;
        }

        console.log('🚀 addFromUrl: Using centralized utility for URL:', inputUrl);
        
        // Use centralized product URL processing
        const result = await processProductUrl(inputUrl);
        
        // Handle validation/processing errors
        if (!result.success) {
            if (result.errorKey === 'modals.bannedContent') {
                setShowBannedModal(true);
                resetAddBarFor(wishlistId);
                return;
            }
            
            if (result.needsManualMode) {
                console.log('⚠️ Auto-load failed, opening manual modal');
                setManualError(t(result.errorKey) || t('errors.autoLoadError'));
                setManualTitle("");
                setManualImage("");
                setManualModalOpenId(wishlistId);
                setManualUrl(inputUrl);
                return;
            }
            
            setCurrentError(t(result.errorKey) || t('errors.invalidLink'));
            return;
        }

        // Success - add product to wishlist
        const item = {
            title: result.title,
            image: result.image,
            url: result.affiliateUrl || inputUrl,
            reservedBy: null
        };

        try {
            const isFirstProduct = !wishlist?.products || wishlist.products.length === 0;

            await updateDoc(doc(db, "wishlists", wishlistId), {
                products: arrayUnion(item)
            });

            if (isFirstProduct) {
                handleFirstProductAdded(wishlistId);
            }

            setCurrentError("");
            resetAddBarFor(wishlistId);
            console.log('✅ addFromUrl: Product added successfully via centralized utility');
        } catch (e) {
            console.error("Error saving product:", e);
            setManualError(t('errors.autoLoadError'));
            setManualTitle("");
            setManualImage("");
            setManualModalOpenId(wishlistId);
            setManualUrl(inputUrl);
        }
    };

  // Check storage limit before proceeding
  const addManualProduct = async (wishlistId) => {
    if (!manualTitle.trim()) {
      setManualError(t('errors.titleRequired'));
      return;
    }

    // Check storage limit before proceeding
    const wishlist = wishlists.find(w => w.id === wishlistId);
    if (wishlist && manualImageBlob && !allowAddWithoutImage) {
      setSpaceLimitWishlistId(wishlistId);
      setShowSpaceLimitModal(true);
      return;
    }

    if (!manualImageBlob && !allowAddWithoutImage) {
      setManualError(t('errors.imageRequired'));
      return;
    }

    // Proceed with adding the product (logic similar to handleAddProduct)
    // ... (rest of the logic for adding manual product)
  };

  // Effect for periodic database limit checking
  useEffect(() => {
    if (!auth.currentUser) return;

    // Check database limits on component mount
    checkDatabaseLimits();

    // Set up periodic checking (every 5 minutes)
    const intervalId = setInterval(() => {
      checkDatabaseLimits();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [auth.currentUser, isPremium]);


  return (
    <div style={{ fontFamily: "Poppins, sans-serif", background: "#fff", minHeight: "100vh", paddingBottom: "180px" }}>
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999,
        transform: navbarVisible ? "translateY(0)" : "translateY(-100%)",
        transition: "transform 0.3s ease-in-out"
      }}>
        <Navbar />
      </div>
      <div style={{ paddingTop: "120px", paddingLeft: "20px", paddingRight: "20px" }}>
      {!showCreateModal && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
          <button
              onClick={() => setShowCreateModal(true)}
              style={{
                width: "100%",
                padding: "16px",
                fontSize: "16px",
                background: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
              }}
            >
              + {t('wishlist.create')}
            </button>

          <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "16px",
                fontSize: "16px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#f5f5f5",
                marginBottom: "20px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                boxSizing: "border-box"
              }}
            />

          {showCollaboratorsModal && (
        <CollaboratorsModal
          wishlistId={showCollaboratorsModal}
          wishlist={wishlists.find(w => w.id === showCollaboratorsModal)}
          currentUserId={auth.currentUser?.uid}
          onClose={() => setShowCollaboratorsModal(null)}
          viewOnly={false}
        />
      )}

      {showLikesModal && (
        <LikesModal
          isOpen={true}
          wishlistId={showLikesModal.wishlistId}
          wishlistName={showLikesModal.wishlistName}
          onClose={() => setShowLikesModal(null)}
        />
      )}

          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "14px"
          }}>
            <div style={{ fontSize: "15px", fontWeight:"600", color: "#000", display: "flex", gap: "20px" }}>
              <span>{t('profile.followers')}: {followers}</span>
              <span>{t('profile.following')}: {following}</span>
            </div>

            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                fontSize: "14px",
                background: "#fff",
                cursor: "pointer",
                color: "#000"
              }}
            >
              <option value="recenti">{t('feed.sortRecent')}</option>
              <option value="popolari">{t('feed.sortPopular')}</option>
            </select>
          </div>

          {/* ⚡ LOADING SKELETON */}
          {isLoadingWishlists ? (
            <>
              {[1, 2, 3].map(i => (
                <div key={i} style={{
                  background: "#f8f8f8",
                  border: "1px solid #e0e0e0",
                  borderRadius: "16px",
                  padding: "16px",
                  marginBottom: "24px",
                  animation: "pulse 1.5s ease-in-out infinite"
                }}>
                  <div style={{ height: "24px", background: "#e0e0e0", borderRadius: "8px", width: "60%", marginBottom: "12px" }}></div>
                  <div style={{ height: "16px", background: "#e0e0e0", borderRadius: "6px", width: "40%", marginBottom: "16px" }}></div>
                  {[1, 2].map(j => (
                    <div key={j} style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                      <div style={{ width: "80px", height: "80px", background: "#e0e0e0", borderRadius: "8px" }}></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ height: "16px", background: "#e0e0e0", borderRadius: "4px", marginBottom: "8px" }}></div>
                        <div style={{ height: "12px", background: "#e0e0e0", borderRadius: "4px", width: "70%" }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          ) : wishlists.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#666",
              fontSize: "16px"
            }}>
              <div style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#333"
              }}>
                {t('home.emptyTitle')}
              </div>
              <div style={{
                fontSize: "14px",
                color: "#666"
              }}>
                {t('home.emptyDescription')}
              </div>
            </div>
          ) : null}

          {!isLoadingWishlists && [...wishlists]
            .filter(w => {
              if (!searchTerm) return true;
              const searchLower = searchTerm.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
              const nameMatch = w.name?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(searchLower);
              const productMatch = w.products?.some(product =>
                product.title?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(searchLower)
              );
              return nameMatch || productMatch;
            })
            .sort((a, b) => {
              if (sortOption === "popolari") {
                return LikesManager.getLikesCount(b) - LikesManager.getLikesCount(a);
              } else {
                return b.createdAt?.seconds - a.createdAt?.seconds;
              }
            })
            .map(w => {
              const expanded = expandedLists[w.id];
              // Products are now stored newest-first in database, no sorting needed
              const sortedProducts = w.products || [];
              const visibleProducts = expanded ? sortedProducts : sortedProducts.slice(0, 3);
              const countdown = getCountdown(w.eventDate);
              const canEdit = w.isOwner || (w.collaborators && w.collaborators.includes(auth.currentUser?.uid));

              return (
                <div
                  key={w.id}
                  data-wishlist-id={w.id}
                  style={{
                    background: "#fefefe",
                    border: "1px solid #ccc",
                    borderRadius: "16px",
                    padding: "16px",
                    marginBottom: "24px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                    animation: "fadeIn 0.3s ease",
                    color: w.isShared ? "#000" : "#000"
                  }}
                >
                  {w.eventDate &&(
                    <div style={{
                      fontSize: "13px",
                      marginBottom: "8px",
                      fontWeight: "bold",
                      color: "#d00",
                      backgroundColor: "#ffe5e5",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      display: "inline-block"
                    }}>
                      {w.eventName ? `${w.eventName}: ${countdown}` : `Event: ${countdown}`}
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <h3
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          margin: 0,
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "#000"
                        }}
                        onClick={() => {
                          sessionStorage.setItem('fromScroll', window.scrollY.toString());
                          sessionStorage.setItem('fromPage', '/');
                          navigate(`/wishlist/${w.id}`, { state: { from: "/" } });
                        }}
                      >
                        {w.name}
                      </h3>
                      {!w.isOwner && (
                        <span style={{
                          fontSize: "12px",
                          background: "#e3f2fd",
                          color: "#1565c0",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontWeight: "500"
                        }}>
                          {t('wishlist.collaborator')}
                        </span>
                      )}
                      {(w.isShared || (w.collaborators && w.collaborators.length > 0)) && (
                        <div
                          onClick={() => setShowCollaboratorsModal(w.id)}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                            border: "2px solid #000",
                            borderRadius: "50%",
                            backgroundColor: "transparent"
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                          </svg>
                        </div>
                      )}
                       <span
                         style={{
                           cursor: "pointer",
                           fontSize: "14px",
                           fontWeight: "500"
                         }}
                         onClick={(e) => {
                           e.stopPropagation();
                           sessionStorage.setItem('fromScroll', window.scrollY.toString());
                           sessionStorage.setItem('fromPage', '/');
                           navigate(`/utente/${w.userId}`, { state: { from: "/" } });
                         }}
                       >
                         {w.userName}
                       </span>
                    </div>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <span 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "4px", 
                          color: "#000", 
                          fontSize: "14px",
                          cursor: (w.isOwner || w.collaborators?.includes(currentUserId)) ? "pointer" : "default",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          transition: "background-color 0.2s ease"
                        }}
                        onClick={(e) => {
                          if (w.isOwner || w.collaborators?.includes(currentUserId)) {
                            e.stopPropagation();
                            setShowLikesModal({ wishlistId: w.id, wishlistName: w.name });
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (w.isOwner || w.collaborators?.includes(currentUserId)) {
                            e.currentTarget.style.backgroundColor = "#f5f5f5";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <FaHeart style={{ color: "#000" }} />
                        {LikesManager.getLikesCount(w)}
                      </span>
                      {w.isOwner && (
                        <button
                          onClick={() => setPrivacyConfirmId(w.id)}
                          style={{
                            background: "#000",
                            color: "#fff",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            fontSize: "13px",
                            fontWeight: "500",
                            border: "none"
                          }}
                        >
                          {w.isPublic ? t('wishlist.public') : t('wishlist.private')}
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => setMenuOpenId(menuOpenId === w.id ? null : w.id)}
                          style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "18px",
                            cursor: "pointer",
                            color: "#000"
                          }}
                        >
                          ⋮
                        </button>
                      )}
                    </div>
                  </div>

                  {(w.tags || []).length > 0 && (
                    <div style={{
                      marginTop: "10px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px"
                    }}>
                      {w.tags.map((tag, i) => (
                        <span key={i} style={{
                          fontSize: "12px",
                          background: "#eee",
                          padding: "4px 10px",
                          borderRadius: "20px",
                          color: "#333"
                        }}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {menuOpenId === w.id && canEdit && (
                    <div style={{
                      marginTop: "12px",
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      padding: "10px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
                    }}>
                      {w.isOwner && (
                        <div onClick={() => {
                          setShowCollaboratorsModal(w.id);
                          setMenuOpenId(null);
                        }} style={{ cursor: "pointer", padding: "6px 0" }}>
                          {(w.isShared || (w.collaborators && w.collaborators.length > 0)) ? t('wishlist.manageCollaborators') : t('wishlist.createShared')}
                        </div>
                      )}
                      {w.isOwner && (
                        <div
                          onClick={() => {
                            setEditWishlistId(w.id);
                            setEditName(w.name || "");
                            setEditTags((w.tags || []).join(", "));
                            setEditEventName(w.eventName || "");
                            setEditEventDate(w.eventDate ? new Date(w.eventDate).toISOString().split("T")[0] : "");
                            setMenuOpenId(null);
                          }}
                          style={{ cursor: "pointer", padding: "6px 0" }}
                        >
                          {t('common.edit')}
                        </div>
                      )}
                      {w.isOwner && (
                        <div onClick={() => {
                          handleShare(w.id);
                          setMenuOpenId(null);
                        }} style={{ cursor: "pointer", padding: "6px 0" }}>
                          {t('common.share')}
                        </div>
                      )}
                      {!w.isOwner && w.collaborators && w.collaborators.includes(auth.currentUser?.uid) && (
                        <div onClick={() => {
                          handleLeaveWishlist(w.id);
                          setMenuOpenId(null);
                        }} style={{ cursor: "pointer", padding: "6px 0", color: "#c00" }}>
                          {t('wishlist.leaveWishlist')}
                        </div>
                      )}
                      {w.isOwner && (
                        <>
                          <div onClick={() => {
                            setArchiveConfirmId(w.id);
                            setMenuOpenId(null);
                          }} style={{ cursor: "pointer", padding: "6px 0" }}>
                            {t('wishlist.archive')}
                          </div>
                          <div onClick={() => {
                            setDeleteConfirmId(w.id);
                            setMenuOpenId(null);
                          }} style={{ cursor: "pointer", padding: "6px 0", color: "#c00" }}>
                            {t('common.delete')}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {editTagsId === w.id && (
                    <div style={{ marginTop: "12px" }}>
                      <input
                        value={editedTags}
                        onChange={(e) => setEditedTags(e.target.value)}
                        placeholder="Modifica tag separati da virgola"
                        style={{
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "14px"
                        }}
                      />

                      {tagError && (
                        <div style={{ color: "#d00", fontSize: "13px", marginTop: "6px" }}>
                          {tagError}
                        </div>
                      )}

                      <div style={{ marginTop: "8px", display: "flex", gap: "10px" }}>
                        <button onClick={() => handleUpdateTags(w.id)} style={{
                          background: "#000", color: "#fff", borderRadius: "6px",
                          padding: "6px 10px", border: "none"
                        }}>
                          Salva
                        </button>
                        <button onClick={() => setEditTagsId(null)} style={{
                          background: "#aaa", color: "#fff", borderRadius: "6px",
                          padding: "6px 10px", border: "none"
                        }}>
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    {(visibleProducts || []).map((p, i) => (
                      <div key={i} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "10px",
                        paddingRight: "50px",
                        border: "1px solid #ddd",
                        transition: "transform 0.2s ease",
                        position: "relative"
                      }}>
                        <div style={styles.productImageContainer}>
                            {(() => {
                              // Robust image source fallback
                              const imageSrc =
                                p?.imageUrl?.trim?.() ||
                                p?.image?.trim?.() ||
                                p?.image_url?.trim?.() ||
                                null;

                              return imageSrc ? (
                                <img
                                  src={imageSrc}
                                  alt={p.title}
                                  style={styles.productImage}
                                  loading="lazy"
                                  onClick={() => handleProductClick(p)}
                                />
                              ) : (
                                <div
                                  style={styles.noImageContainer}
                                  onClick={() => canEdit ? setAddImageModal({
                                  show: true,
                                  productId: p.id || `${p.title}-${Date.now()}`,
                                  wishlistId: w.id,
                                  productTitle: p.title,
                                  productUrl: p.url
                                }) : null}
                                  aria-label={canEdit ? t('images.addImage') : t('images.noImage')}
                                >
                                  {canEdit ? (
                                    <>
                                      <svg style={styles.addImageIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="12" y1="8" x2="12" y2="16"/>
                                        <line x1="8" y1="12" x2="16" y2="12"/>
                                      </svg>
                                      <span style={styles.addImageText}>{t('images.addImage')}</span>
                                    </>
                                  ) : (
                                    <span style={styles.noImageText}>{t('images.noImage')}</span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        <div style={{ flex: 1 }}>
                          <a
                            href={getAffiliateLink(p)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontWeight: "500",
                              fontSize: "16px",
                              textDecoration: "none",
                              color: "#000",
                              flex: 1,
                              marginRight: "8px",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              wordBreak: "break-all",
                              hyphens: "auto",
                              lineHeight: "1.3",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              maxWidth: "100%"
                            }}
                            onClick={() => trackClick(p.id, w.id, 'amazon')}
                          >
                            {p.title}
                          </a>
                        </div>
                        <button onClick={() => {
                          setProductToDelete({
                            productIndex: i,
                            wishlistId: w.id,
                            productTitle: p.title
                          });
                          setShowConfirmModal(true);
                        }} style={{
                          background: "#f0f0f0",
                          color: "#666",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          width: "32px",
                          height: "32px",
                          fontSize: "18px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          flexShrink: 0,
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)"
                        }}>
                          −
                        </button>
                      </div>
                    ))}
                    {(w.products?.length || 0) > 3 && (
                      <button onClick={() => toggleExpand(w.id)} style={{
                        fontSize: "14px",
                        background: "none",
                        border: "none",
                        color: "#000",
                        textDecoration: "underline",
                        cursor: "pointer",
                        alignSelf: "flex-start"
                      }}>
                        {expanded ? t('wishlist.showLess') : `+ ${w.products.length - 3} ${t('wishlist.moreProducts')}`}
                      </button>
                    )}

                    {addingProductId === w.id ? (
                      <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                        <input
                          style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #ccc" }}
                          placeholder="Product link"
                          value={newProductLinks[w.id] || ""}
                          onChange={e => setNewProductLinks({ ...newProductLinks, [w.id]: e.target.value })}
                        />
                        <button onClick={() => handleAddProduct(w.id)} style={{
                          background: "#000", color: "#fff", borderRadius: "8px",
                          padding: "8px 14px", border: "none"
                        }}>
                          {t('common.add')}
                        </button>
                        <button onClick={() => {
                          resetAddBarFor(w.id); // 🧹 Clear input and collapse bar on cancel
                        }} style={{
                          background: "#aaa", color: "#fff", borderRadius: "8px",
                          padding: "8px 14px", border: "none"
                        }}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => {
                        setAddingProductId(w.id);
                        setInputUrl("");
                        setCurrentError("");
                      }} style={{
                        marginTop: "10px",
                        background: "#000", color: "#fff", border: "none",
                        padding: "10px 16px", borderRadius: "8px", cursor: "pointer"
                      }}>
                        + {t('wishlist.addProduct')}
                      </button>
                    )}



                    {manualModeId ===w.id && (
                      <div style={{ marginTop: "10px" }}>
                        <input
                          type="text"
                          placeholder={t('wishlist.productTitle')}
                          value={manualTitle}
                          onChange={(e) => setManualTitle(e.target.value)}
                          style={{ width: "100%", padding: "14px", marginBottom: "16px", borderRadius:"8px", border: "1px solid #ccc", fontSize: "15px", background: "#fff" }}
                        />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            setManualError("");

                            if (!file) {
                              setManualError("Seleziona un'immagine");
                              return;
                            }

                            // Check file size before processing (100MB max - we'll compress it down)
                            if (file.size > 100 * 1024 * 1024) {
                              setManualError(t('errors.imageTooBig'));
                              return;
                            }

                            try {
                              console.log(`🗜️ Processing image with ultra compression: ${file.name}, ${Math.round(file.size / 1024)}KB`);

                              // Use unified image pipeline with ultra-aggressive compression
                              const prepared = await prepareImageBlob(file);
                              console.log(`✅ Ultra-compressed to: ${Math.round(prepared.blob.size / 1024)}KB (${((1 - prepared.blob.size / file.size) * 100).toFixed(1)}% reduction)`);

                              // Moderate the processed image
                              const isAllowed = await moderateImageBlob(prepared.blob);
                              if (!isAllowed) {
                                setManualError(t('errors.imageNotAllowed'));
                                setManualImage("");
                                return;
                              }

                              // Convert to base64 for display
                              const arrayBuffer = await prepared.blob.arrayBuffer();
                              const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
                              const dataUrl = `data:${prepared.mime};base64,${base64}`;

                              setManualImage(dataUrl);
                              setManualError("");
                              console.log("✅ Ultra-compressed image ready for storage");

                            } catch (error) {
                              console.error("❌ Error processing image:", error);
                              if (error === 'cors') {
                                setManualError("📱 Errore nel caricamento dell'immagine. Prova a scattare una nuova foto.");
                              } else if (error === 'invalid_mime') {
                                setManualError("📱 Formato immagine non supportato. Usa JPG o PNG.");
                              } else {
                                setManualError(t('errors.imageProcessingError'));
                              }
                            }
                          }}
                          style={{ marginBottom: "16px" }}
                        />
                        <div style={{ display: "flex", gap: "10px" }}>
                          <button
                            onClick={async () => {
                              const item = {
                                title: manualTitle || t('wishlist.addProduct'),
                                image: manualImage || "",
                                url: newProductLinks[w.id] || "",
                                reservedBy: null
                              };
                              // Verifica se è il primo prodotto prima di aggiungere
                              const isFirstProduct = !w.products || w.products.length === 0;

                              await updateDoc(doc(db, "wishlists", w.id), {
                                products: arrayUnion(item)
                              });

                              // Mostra il modale solo se è il primo prodotto
                              if (isFirstProduct) {
                                handleFirstProductAdded(w.id);
                              }

                              setManualModeId(null);
                              resetAddBarFor(w.id); // 🧹 Clear input and collapse bar on success
                            }}
                            style={{
                              background: "#000",
                              color: "#fff",
                              border: "none",
                              padding: "16px",
                              borderRadius: "8px",
                              fontSize: "16px",
                              fontWeight: 600,
                              width: "48%",
                              cursor: "pointer",
                              transition: "all 0.2s ease"
                            }}
                          >
                            {t('common.save')}
                          </button>
                          <button
                            onClick={() => {
                              resetAddBar();
                            }}
                            style={{
                              background: "#aaa",
                              color: "#fff",
                              border: "none",
                              padding: "16px",
                              borderRadius: "8px",
                              fontSize: "16px",
                              fontWeight: 600,
                              width: "48%",
                              cursor: "pointer",
                              transition: "all 0.2s ease"
                            }}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {manualModalOpenId && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.6)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999
        }} onClick={() => setManualModalOpenId(null)}>
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "16px",
              width: "90%",
              maxWidth: "400px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
              textAlign: "center",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
              transform: "translate(0, 0)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {manualError === "Questo link non è consentito. Prova con un altro." ? (
              <>
                <h2 style={{ marginBottom: "16px", fontSize: "20px", fontWeight: "600" }}>
                  {t('modals.linkNotAllowed')}
                </h2>
                <div style={{ color: "#d00", fontSize: "14px", marginBottom: "24px" }}>
                  {manualError}
                </div>
                <button
                  onClick={() => {
                    setManualModalOpenId(null);
                    setManualTitle("");
                    setManualImage("");
                    setManualUrl("");
                    setManualError("");
                  }}
                  style={styles.cancelBtn}
                >
                  {t('common.close')}
                </button>
              </>
            ) : (
              <>
                <h2 style={{ marginBottom: "16px", fontSize: "20px", fontWeight: "600" }}>
                  {t('addProductModal.insertManually')}
                </h2>

                {manualError && (
                  <p style={{ color: '#D32F2F', marginTop: 8, marginBottom: 16, fontSize: "14px" }}>
                    {tSafe(manualError)}
                  </p>
                )}

                <input
                  value={manualTitle}
                  onChange={(e) => setManualTitle(e.target.value)}
                  placeholder={t('addProductModal.productTitle')}
                  style={styles.input}
                />

                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    setManualError("");

                    if (!file) {
                      setManualError("Seleziona un'immagine");
                      return;
                    }

                    // Check file size before processing (25MB max for better mobile performance)
                    if (file.size > 25 * 1024 * 1024) {
                      setManualError(t('errors.imageTooLarge'));
                      return;
                    }

                    // Show immediate feedback
                    setManualError("");

                    try {
                      console.log(`🗜️ Processing image: ${file.name}, ${Math.round(file.size / 1024)}KB`);

                      // Use unified image pipeline with timeout
                      const timeoutPromise = new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('PROCESSING_TIMEOUT')), 30000)
                      );

                      const processPromise = prepareImageBlob(file);
                      const prepared = await Promise.race([processPromise, timeoutPromise]);

                      console.log(`✅ Image processed: ${Math.round(prepared.blob.size / 1024)}KB (${((1 - prepared.blob.size / file.size) * 100).toFixed(1)}% reduction)`);

                      // Moderate the processed image
                      const isAllowed = await moderateImageBlob(prepared.blob);
                      if (!isAllowed) {
                        setManualError(t('errors.imageNotAllowed'));
                        setManualImage("");
                        return;
                      }

                      // Convert to base64 for display and storage
                      const arrayBuffer = await prepared.blob.arrayBuffer();
                      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
                      const dataUrl = `data:${prepared.mime};base64,${base64}`;

                      setManualImage(dataUrl);
                      setManualError("");
                      console.log("✅ Image ready for storage");

                    } catch (error) {
                      console.error("❌ Error processing image:", error);
                      let errorMessage = "📱 Errore nell'elaborazione dell'immagine.";

                      if (error.message === 'PROCESSING_TIMEOUT') {
                        errorMessage = t('errors.imageProcessingSlow');
                      } else if (error === 'cors') {
                        errorMessage = "📱 Errore nel caricamento dell'immagine. Prova a scattare una nuova foto.";
                      } else if (error === 'invalid_mime') {
                        errorMessage = "📱 Formato immagine non supportato. Usa JPG o PNG.";
                      } else if (error.message?.includes('size') || error.message?.includes('large')) {
                        errorMessage = t('errors.imageTooBig');
                      }

                      setManualError(errorMessage);
                      setManualImage("");
                    }
                  }}
                  style={{ marginBottom: "16px" }}
                />

                {manualImage && (
                  <div style={{ marginBottom: "16px", textAlign: "center" }}>
                    <img
                      src={manualImage}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "120px",
                        borderRadius: "8px",
                        objectFit: "cover"
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        setManualError(t('addProductModal.imageLoadError'));
                      }}
                      onLoad={(e) => {
                        e.target.style.display = 'block';
                        if (manualError === t('addProductModal.imageLoadError')) {
                          setManualError("");
                        }
                      }}
                    />
                  </div>
                )}

                <div style={{ fontSize: "13px", color: "#666", marginBottom: "16px", lineHeight: "1.4" }}>
                  {t('addProductModal.disclaimer')}
                </div>


                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginTop: "24px" }}>
                  <button
                    onClick={handleManualSubmit}
                    disabled={manualLoading}
                    style={{
                      ...styles.confirmBtn,
                      opacity: manualLoading ? 0.6 : 1,
                      cursor: manualLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {manualLoading ? t('common.loading') : t('common.add')}
                  </button>

                  <button
                    onClick={() => {
                      setManualError('');
                      setManualLoading(false);
                      resetAddBar();
                    }}
                    disabled={manualLoading}
                    style={{
                      ...styles.cancelBtn,
                      opacity: manualLoading ? 0.6 : 1,
                      cursor: manualLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showCreateModal && (
        <div style={styles.overlay} onClick={() => setShowCreateModal(false)}>
          <div
            style={{
              ...styles.modal,
              maxWidth: "95%",
              width: "400px",
              height: "92vh",
              overflowY: "auto",
              padding: "28px"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: "24px", fontSize: "20px", fontWeight: "600" }}>{t('wishlist.create')}</h2>

            <input
              value={newWishlistName}
              onChange={e => setNewWishlistName(e.target.value)}
              placeholder={t('wishlist.name')}
              style={styles.input}
            />
            <div>
              <input
                type="text"
                placeholder={t('wishlist.tags')}
                value={newWishlistTags}
                onChange={(e) => {
                  setNewWishlistTags(e.target.value);
                  setTagError("");
                }}
                style={{
                  ...styles.input,
                  marginBottom: "4px",
                  borderColor: tagError ? "#ff4444" : "#ccc"
                }}
              />
              <div style={{ fontSize: "12px", color: getTagCount(newWishlistTags) > 5 ? "#ff4444" : "#666", marginBottom: "10px" }}>
                {getTagCount(newWishlistTags)}/5 tag
              </div>
              {tagError && (
                <div style={{ color: "#d00", fontSize: "13px", marginBottom: "10px" }}>
                  {tagError}
                </div>
              )}
            </div>
            <input
              value={newWishlistEventName}
              onChange={e => setNewWishlistEventName(e.target.value)}
              placeholder={t('wishlist.eventName')}
              style={styles.input}
            />
            <input
              type="date"
              value={newWishlistDate}
              onChange={e => setNewWishlistDate(e.target.value)}
              style={styles.input}
            />

            <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "24px", width: "100%" }}>
              <button onClick={handleCreateWishlist} style={styles.confirmBtn}>{t('wishlist.create')}</button>
              <button onClick={() => setShowCreateModal(false)} style={styles.cancelBtn}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {editWishlistId && (
        <div style={styles.overlay} onClick={() => setEditWishlistId(null)}>
          <div
            style={{ ...styles.modal, maxWidth: "90%", width: "400px", maxHeight: "80vh", overflowY: "auto", padding: "20px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: "24px", fontSize: "20px", fontWeight: "600" }}>{t('wishlist.edit')}</h2>

            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={t('wishlist.name')}
              style={styles.input}
            />
            <div>
              <input
                type="text"
                placeholder={t('wishlist.tags')}
                value={editTags}
                onChange={(e) => {
                  setEditTags(e.target.value);
                  setTagError("");
                }}
                style={{
                  ...styles.input,
                  marginBottom: "4px",
                  borderColor: tagError ? "#ff4444" : "#ccc"
                }}
              />
              <div style={{ fontSize: "12px", color: getTagCount(editTags) > 5 ? "#ff4444" : "#666", marginBottom: "10px" }}>
                {getTagCount(editTags)}/5 tag
              </div>
              {tagError && (
                <div style={{ color: "#d00", fontSize: "13px", marginBottom: "10px" }}>
                  {tagError}
                </div>
              )}
            </div>
            <input
              value={editEventName}
              onChange={(e) => setEditEventName(e.target.value)}
              placeholder={t('wishlist.eventName')}
              style={styles.input}
            />
            <input
              type="date"
              value={editEventDate}
              onChange={e => setEditEventDate(e.target.value)}
              style={styles.input}
            />
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "24px", width: "100%" }}>
              <button
                onClick={async () => {
                  const tagsArray = editTags.split(",").map(t => t.trim()).filter(Boolean);
                    const tagValidation = validateTags(editTags);
                    if (!tagValidation.isValid) {
                      setTagError(t(`errors.${tagValidation.error}`));
                      return;
                    }
                  if (tagsArray.length > 5) {
                      setTagError(t('errors.tagLimit'));
                    return;
                  }

                  const eventDate = editEventDate ? new Date(editEventDate + 'T23:59:59').toISOString() : null;

                  await updateDoc(doc(db, "wishlists", editWishlistId), {
                    name: editName.trim(),
                    tags: tagsArray,
                    eventName: editEventName.trim(),
                    eventDate: eventDate
                  });

                  setEditWishlistId(null);
                  setTagError("");
                }}
                style={{ ...styles.confirmBtn, flex: 1 }}
              >
                {t('common.save')}
              </button>
              <button onClick={() => setEditWishlistId(null)} style={{ ...styles.cancelBtn, flex: 1 }}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmId && (
        <div style={styles.overlay} onClick={() => setDeleteConfirmId(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p>{t('modals.deleteWishlist')}</p>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "16px" }}>
              <button onClick={confirmDeleteWishlist} style={styles.confirmBtn}>{t('common.confirm')}</button>
              <button onClick={() => setDeleteConfirmId(null)} style={styles.cancelBtn}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {shareModalId && (
        <div style={styles.shareOverlay} onClick={() => setShareModalId(null)}>
          <div style={styles.shareModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.shareHeader}>
              <h3 style={styles.shareTitle}>
                <FaShare style={{ marginRight: "8px" }} />
                {t('share.shareWishlist')}
                </h3>
                        </div>

            <div style={styles.shareContent}>
              <p style={styles.shareLabel}>{t('wishlist.shareLink')}:</p>
              <div style={styles.linkContainer}>                <input
                  type="text"
                  readOnly
                  value={getShareableWishlistUrl(shareModalId)}
                  onFocus={(e) => e.target.select()}
                  style={styles.shareInput}
                />
                <button onClick={handleCopyLink} style={styles.copyButton}>
                  {t('wishlist.copyLink')}
                </button>
              </div>

              {copySuccess && (
                <p style={styles.successMessage}>{t('common.copied')}</p>
              )}

              <div style={styles.socialContainer}>
                <p style={styles.socialLabel}>{t('share.shareSocial')}:</p>
                <div style={styles.socialGrid}>
                  <button
                    style={{ ...styles.socialBtn, backgroundColor: "#1877f2" }}
                    onClick={() => handleSocialShare('facebook')}
                  >
                    <FaFacebook size={16} />
                    <span>Facebook</span>
                  </button>
                  <button
                    style={{ ...styles.socialBtn, backgroundColor: "#25d366" }}
                    onClick={() => handleSocialShare('whatsapp')}
                  >
                    <FaWhatsapp size={16} />
                    <span>WhatsApp</span>
                  </button>
                  <button
                    style={{ ...styles.socialBtn, backgroundColor: "#000" }}
                    onClick={() => handleSocialShare('twitter')}
                  >
                    <FaXTwitter size={16} />
                    <span>X</span>
                  </button>
                  <button
                    style={{ ...styles.socialBtn, backgroundColor: "#e4405f" }}
                    onClick={() => handleSocialShare('instagram')}
                  >
                    <FaInstagram size={16} />
                    <span>Instagram</span>
                  </button>
                  <button
                    style={{ ...styles.socialBtn, backgroundColor: "#000" }}
                    onClick={() => handleSocialShare('tiktok')}
                  >
                    <FaTiktok size={16} />
                    <span>TikTok</span>
                  </button>
                </div>
              </div>

              <button onClick={() => setShareModalId(null)} style={styles.closeShareBtn}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {privacyConfirmId && (
        <div style={styles.overlay} onClick={() => setPrivacyConfirmId(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p>{t('modals.changeVisibility')}</p>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "16px" }}>
              <button onClick={confirmTogglePrivacy} style={styles.confirmBtn}>{t('common.confirm')}</button>
              <button onClick={() => setPrivacyConfirmId(null)} style={styles.cancelBtn}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {archiveConfirmId && (
        <div style={styles.overlay} onClick={() => setArchiveConfirmId(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p>{t('modals.archiveWishlist')}</p>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "16px" }}>
              <button onClick={confirmArchiveWishlist} style={styles.confirmBtn}>{t('common.confirm')}</button>
              <button onClick={() => setArchiveConfirmId(null)} style={styles.cancelBtn}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {showBannedModal && (
        <div style={styles.bannedOverlayStyle} onClick={() => setShowBannedModal(false)}>
          <div style={styles.bannedModalStyle} onClick={(e) => e.stopPropagation()}>
            <p style={{ fontWeight: "500", fontSize: "16px", marginBottom: "20px" }}>
              {t('modals.bannedContent')}
            </p>
            <button
              style={{
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                padding: "10px 24px",
                borderRadius: "8px",
                fontSize: "15px",
                cursor: "pointer",
              }}
              onClick={() => setShowBannedModal(false)}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

        {showFirstProductModal && (
          <div style={styles.firstProductOverlay}>
            <div style={styles.firstProductModal}>
              <h2 style={styles.title}>{t('modals.wishlistComesToLife')}</h2>
              <p style={styles.text}>
                {t('modals.firstProductAdded')}
              </p>
              <div style={styles.buttonContainer}>
                <button style={styles.buttonBlack} onClick={makeWishlistPublic}>
                  {t('modals.makePublic')}
                </button>
                <button style={styles.buttonWhite} onClick={closeFirstProductModal}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
        {showOnboarding && <Onboarding onClose={() => setShowOnboarding(false)} />}

        {socialInstructionsModal && (
          <div style={styles.socialInstructionsOverlay} onClick={() => setSocialInstructionsModal(null)}>
            <div style={styles.socialInstructionsModal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.socialInstructionsHeader}>
                <h3 style={styles.socialInstructionsTitle}>
                  {t(`share.socialInstructions.${socialInstructionsModal}.title`)}
                                </h3>
                <button
                  onClick={() => setSocialInstructionsModal(null)}
                  style={styles.closeButton}
                >
                  ×
                </button>
              </div>

              <div style={styles.socialInstructionsContent}>
                <div style={styles.successBadge}>
                  <span style={styles.successIcon}>✓</span>
                  {t('share.linkCopiedSuccess')}
                </div>

                <p style={styles.instructionText}>
                  {t(`share.socialInstructions.${socialInstructionsModal}.instruction`)}
                </p>

                <div style={styles.stepsList}>
                  <div style={styles.step}>
                    {t(`share.socialInstructions.${socialInstructionsModal}.step1`)}
                  </div>
                  <div style={styles.step}>
                    {t(`share.socialInstructions.${socialInstructionsModal}.step2`)}
                  </div>
                  <div style={styles.step}>
                    {t(`share.socialInstructions.${socialInstructionsModal}.step3`)}
                  </div>
                  <div style={styles.step}>
                    {t(`share.socialInstructions.${socialInstructionsModal}.step4`)}
                  </div>
                </div>

                <button
                  onClick={() => setSocialInstructionsModal(null)}
                  style={styles.gotItButton}
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}

      <PremiumModal
        isOpen={showPremiumModal || wishlistLimitReached}
        onClose={() => {
          setShowPremiumModal(false);
          setWishlistLimitReached(false);
        }}
        reason={wishlistLimitReached ? 'limit' : 'general'}
      />

      {/* Modale limite lifetime */}
      {showLifetimeLimitModal && (
        <div style={styles.overlay} onClick={() => setShowLifetimeLimitModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: "16px", fontSize: "18px", fontWeight: "600", color: "#333" }}>
              {t('modals.lifetimeLimitReached')}
            </h2>
            <p style={{ marginBottom: "20px", fontSize: "15px", lineHeight: "1.4", color: "#666" }}>
              {t('modals.lifetimeLimitDescription')}
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "16px" }}>
              <button
                onClick={() => setShowLifetimeLimitModal(false)}
                style={{
                  background: "#000",
                  color: "#fff",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer"
                }}
              >
                {t('modals.understood')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wishlist Space Limit Modal */}
      <WishlistSpaceLimitModal
        isOpen={showSpaceLimitModal}
        onClose={() => {
          setShowSpaceLimitModal(false);
          setSpaceLimitWishlistId(null);
          setAllowAddWithoutImage(false);
        }}
        onProceedWithoutImage={() => {
          setAllowAddWithoutImage(true);
          setShowSpaceLimitModal(false);
          // Continue with the add operation without image
          if (spaceLimitWishlistId) {
            if (manualModeId) {
              addManualProduct(spaceLimitWishlistId);
            } else if (inputUrl) {
              addFromUrl(spaceLimitWishlistId);
            }
          }
        }}
        onManageProducts={() => {
          setShowSpaceLimitModal(false);
          setSpaceLimitWishlistId(null);
          setAllowAddWithoutImage(false);
          // Could implement product management modal here
        }}
        onCreateNewWishlist={() => {
          setShowSpaceLimitModal(false);
          setSpaceLimitWishlistId(null);
          setAllowAddWithoutImage(false);
          setIsCreatingWishlist(true);
        }}
      />

      {/* Modal conferma eliminazione prodotto */}
      {showConfirmModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.confirmModal}>
            <h3 style={styles.confirmTitle}>
              {t('modals.deleteProduct')}
            </h3>
            <p style={styles.confirmText}>
              {t('modals.deleteProductConfirm')} "{productToDelete?.productTitle}"?
            </p>
            <div style={styles.confirmButtons}>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setProductToDelete(null);
                }}
                style={styles.cancelButton}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteProduct}
                style={styles.deleteButton}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal per aggiungere immagine */}
      {addImageModal.show && (
        <div style={styles.modalOverlay}>
          <div style={styles.confirmModal}>
            <h3 style={styles.confirmTitle}>
              {t('images.addImageDialogTitle')}
            </h3>
            <p style={styles.confirmText}>
              {t('images.addImageDialogDescription')}
            </p>

            {!imageUploadType ? (
              <div style={styles.confirmButtons}>
                <button
                  onClick={() => setImageUploadType('file')}
                  style={styles.primaryButton}
                >
                  {t('images.chooseFromGallery')}
                </button>
                <button
                  onClick={() => setImageUploadType('url')}
                  style={styles.secondaryButton}
                >
                  {t('images.pasteImageUrl')}
                </button>
              </div>
            ) : (
              <div>
                {imageUploadType === 'file' && (
                  <div style={styles.fileInputContainer}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setSelectedImageFile(file);
                        setImageError("");
                        if (file) {
                          // Show preview
                          const reader = new FileReader();
                          reader.onload = (e) => {
                            // Could add preview here if needed
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      style={styles.fileInput}
                    />
                    {selectedImageFile && (
                      <div style={styles.fileInfo}>
                        ✓ {selectedImageFile.name}
                      </div>
                    )}
                  </div>
                )}

                {imageUploadType === 'url' && (
                  <div style={{ marginBottom: "16px" }}>
                    <input
                      type="url"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder={t('images.addImageUrlPlaceholder')}
                      style={{
                        width: "100%",
                        padding: "14px",
                        marginBottom: "16px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        fontSize: "15px",
                        background: "#fff",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                )}

                {imageError && (
                  <div style={styles.errorMessage}>
                    {imageError}
                  </div>
                )}

                <div style={styles.confirmButtons}>
                  <button
                    onClick={() => {
                      setAddImageModal({ show: false, productId: null, wishlistId: null, productTitle: null, productUrl: null });
                      setImageUploadType(null);
                      setNewImageUrl("");
                      setSelectedImageFile(null);
                      setImageError("");
                    }}
                    style={styles.cancelButton}
                    disabled={imageUploadLoading}
                  >
                    {t('images.addImageCancel')}
                  </button>
                  <button
                    onClick={handleAddImage}
                    style={styles.primaryButton}
                    disabled={imageUploadLoading ||
                             (imageUploadType === 'url' && !newImageUrl) ||
                             (imageUploadType === 'file' && !selectedImageFile)}
                  >
                    {imageUploadLoading ? t('common.loading') : t('images.addImageSave')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Database Limit Modal */}
      <DatabaseLimitModal
        show={showDatabaseLimitModal}
        onClose={() => {
          setShowDatabaseLimitModal(false);
          setDatabaseStorageInfo(null);
        }}
        onUpgrade={handleUpgradeToPremium}
        storageInfo={databaseStorageInfo}
        canProceed={false}
      />

      {/* Leave Wishlist Modal */}
      {showLeaveWishlistModal && (
        <div style={styles.overlay} onClick={isLeaving ? undefined : closeLeaveWishlistModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 600 }}>
              {t('modals.leaveWishlistTitle')}
            </h3>
            <p style={{ margin: "0 0 20px 0", fontSize: "15px", lineHeight: "1.5", color: "#666" }}>
              {t('modals.leaveWishlistMessage')}
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={closeLeaveWishlistModal}
                disabled={isLeaving}
                style={{
                  padding: "12px 20px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: isLeaving ? "#f5f5f5" : "#fff",
                  color: isLeaving ? "#999" : "#333",
                  cursor: isLeaving ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                  opacity: isLeaving ? 0.7 : 1
                }}
              >
                {t('modals.leaveWishlistCancel')}
              </button>
              <button
                onClick={confirmLeaveWishlist}
                disabled={isLeaving}
                style={{
                  padding: "12px 20px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: isLeaving ? "#999" : "#000",
                  color: "#fff",
                  cursor: isLeaving ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  opacity: isLeaving ? 0.8 : 1
                }}
              >
                {isLeaving ? t('common.saving') : t('modals.leaveWishlistConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999
  },
  modal: {
    background: "#fff",
    borderRadius: "14px",
    width: "95%",
    maxWidth: "320px",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: "24px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
    fontFamily: "Poppins, sans-serif",
    color: "#000",
    animation: "fadeIn 0.3s ease-in-out",
    zIndex: 1000,
    position: "relative"
  },
  input: {
    width: "100%",
    padding: "14px",
    marginBottom: "16px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    background: "#fff",
    boxSizing: "border-box"
  },
  confirmBtn: {
    background: "#000",
    color: "#fff",
    border: "none",
    padding: "16px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    width: "48%",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  cancelBtn: {
    background: "#aaa",
    color: "#fff",
    border: "none",
    padding: "16px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    width: "48%",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  shareOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
justifyContent: "center",
    zIndex: 999,
    padding: "40px 20px",
    boxSizing: "border-box"
  },
  shareModal: {
    background: "#fff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "350px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
    fontFamily: "Poppins, sans-serif",
    color: "#000",
    animation: "fadeIn 0.3s ease-in-out",
    zIndex: 1000,
    position: "relative"
  },
  shareHeader: {
    padding: "20px 20px 12px 20px",
    borderBottom: "1px solid #f0f0f0"
  },
  shareTitle: {
    fontSize: "18px",
    fontWeight: "600",
    margin: 0,
    display: "flex",
    alignItems: "center",
    color: "#000"
  },
  shareContent: {
    padding: "16px 20px 20px 20px"
  },
  shareLabel: {
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "12px",
    color: "#333"
  },
  linkContainer: {
    display: "flex",
    gap: "10px",
    marginBottom: "16px"
  },
  shareInput: {
    flex: 1,
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #ddd",
    fontSize: "14px",
    background: "#f8f9fa",
    color: "#333"
  },
  copyButton: {
    background: "#000",
    color: "#fff",
    border: "none",
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  successMessage: {
    color: "#22c55e",
    fontSize: "14px",
    fontWeight: "500",
    margin: "8px 0 16px 0",
    textAlign: "center"
  },
  socialContainer: {
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #f0f0f0"
  },
  socialLabel: {
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "12px",
    color: "#333"
  },
  socialGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "20px"
  },
  socialBtn: {
    color: "#fff",
    border: "none",
    padding: "10px 6px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
  },
  closeShareBtn: {
    background: "#f3f4f6",
    color: "#374151",
    border: "none",
    padding: "12px",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    width: "100%",
    cursor: "pointer",
    transition: "background 0.2s ease",
    marginTop: "auto"
  },
  bannedOverlayStyle: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999
  },
  bannedModalStyle: {
    backgroundColor: "#fff",
    padding: "24px",
    borderRadius: "10px",
    textAlign: "center",
    color: "#000",
    width: "85%",
    maxWidth: "340px"
  },
    firstProductOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      height: "100vh",
      width: "100vw",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: "16px",
      boxSizing: "border-box",
    },
    firstProductModal: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "24px",
      width: "100%",
      maxWidth: "320px",
      minWidth: "280px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
      textAlign: "center",
      position: "relative",
      margin: "auto",
    },
    title: {
      fontSize: "1.5rem",
      marginBottom: "0.8rem",
      fontWeight: "bold",
    },
    text: {
      fontSize: "1rem",
      marginBottom: "1.2rem",
    },
    buttonContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "0.8rem",
    },
    buttonBlack: {
      padding: "0.75rem",
      backgroundColor: "#000",
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      fontSize: "1rem",
      cursor: "pointer",
    },
    buttonWhite: {
      padding: "0.75rem",
      backgroundColor: "#fff",
      color: "#000",
      border: "1px solid #ccc",
      borderRadius: "8px",
      fontSize: "1rem",
      cursor: "pointer",
    },
    socialInstructionsOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000,
      padding: "20px",
      boxSizing: "border-box"
    },
    socialInstructionsModal: {
      background: "#fff",
      borderRadius: "16px",
      width: "90%",
      maxWidth: "320px",
      height: "auto",
      boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
      fontFamily: "Poppins, sans-serif",
      color: "#000",
      animation: "fadeIn 0.3s ease-in-out",
      position: "relative",
      display: "flex",
      flexDirection: "column"
    },
    socialInstructionsHeader: {
      padding: "16px 20px 10px 20px",
      borderBottom: "1px solid #f0f0f0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexShrink: 0
    },
    socialInstructionsTitle: {
      fontSize: "16px",
      fontWeight: "600",
      margin: 0,
      color: "#000"
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "24px",
      cursor: "pointer",
      color: "#666",
      padding: "0",
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    socialInstructionsContent: {
      padding: "12px 20px 16px 20px",
      flex: 1,
      display: "flex",
      flexDirection: "column"
    },
    successBadge: {
      background: "#e8f5e8",
      color: "#2d7d2d",
      padding: "8px 12px",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: "600",
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "6px"
    },
    successIcon: {
      fontSize: "16px",
      fontWeight: "bold"
    },
    instructionText: {
      fontSize: "14px",
      color: "#333",
      marginBottom: "12px",
      lineHeight: "1.4"
    },
    stepsList: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginBottom: "16px",
      flex: 1
    },
    step: {
      fontSize: "13px",
      color: "#555",
      lineHeight: "1.4",
      padding: "4px 0"
    },
    gotItButton: {
      background: "#000",
      color: "#fff",
      border: "none",
      padding: "10px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      width: "100%",
      cursor: "pointer",
      transition: "background 0.2s ease",
      marginTop: "auto"
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999
    },
    confirmModal: {
      backgroundColor: "#fff",
      padding: "24px",
      borderRadius: "12px",
      width: "90%",
      maxWidth: "320px",
      boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
      textAlign: "center",
      fontFamily: "Poppins, sans-serif",
      color: "#000",
      position: "relative"
    },
    confirmTitle: {
      fontSize: "18px",
      fontWeight: "600",
      marginBottom: "16px"
    },
    confirmText: {
      fontSize: "15px",
      color: "#666",
      marginBottom: "20px",
      lineHeight: "1.4"
    },
    confirmButtons: {
      display: "flex",
      justifyContent: "space-between",
      gap: "8px",
      marginTop: "16px"
    },
    confirmButtonsVertical: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      marginTop: "16px"
    },
    deleteButton: {
      backgroundColor: "#000",
      color: "#fff",
      border: "none",
      padding: "14px 16px",
      borderRadius: "8px",
      fontSize: "15px",
      cursor: "pointer",
      fontWeight: "600",
      transition: "background 0.2s ease",
      flex: 1,
      minHeight: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    primaryButton: {
      backgroundColor: "#000",
      color: "#fff",
      border: "none",
      padding: "14px 16px",
      borderRadius: "8px",
      fontSize: "15px",
      cursor: "pointer",
      fontWeight: "600",
      transition: "background 0.2s ease",
      flex: 1,
      minHeight: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    secondaryButton: {
      backgroundColor: "#f0f0f0",
      color: "#000",
      border: "1px solid #ddd",
      padding: "14px 16px",
      borderRadius: "8px",
      fontSize: "15px",
      cursor: "pointer",
      fontWeight: "600",
      transition: "background 0.2s ease",
      flex: 1,
      minHeight: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    cancelButton: {
      backgroundColor: "#ccc",
      color: "#fff",
      border: "none",
      padding: "14px 16px",
      borderRadius: "8px",
      fontSize: "15px",
      cursor: "pointer",
      fontWeight: "600",
      transition: "background 0.2s ease",
      flex: 1,
      minHeight: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    fileInputContainer: {
      width: "100%",
      marginBottom: "16px",
      padding: "0"
    },
    fileInput: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: "2px dashed #ddd",
      fontSize: "14px",
      cursor: "pointer",
      backgroundColor: "#f9f9f9",
      boxSizing: "border-box"
    },
    fileInfo: {
      marginTop: "8px",
      fontSize: "14px",
      color: "#28a745",
      fontWeight: "500"
    },
    errorMessage: {
      color: "#e53e3e",
      fontSize: "14px",
      marginBottom: "16px",
      textAlign: "left"
    },
    productImageContainer: {
      width: "100px",
      height: "100px",
      flexShrink: 0,
      position: "relative",
    },
    productImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "8px",
      cursor: "pointer",
    },
    noImageContainer: {
      width: "100%",
      height: "100%",
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      border: "2px dashed #ddd",
      transition: "all 0.3s ease",
    },
    addImageIcon: {
      width: "24px",
      height: "24px",
      color: "#666",
      marginBottom: "8px",
    },
    addImageText: {
      fontSize: "12px",
      color: "#666",
      textAlign: "center",
    },
    noImageText: {
      fontSize: "12px",
      color: "#999",
      textAlign: "center",
    }
  };